import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  Signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import dayjs from 'dayjs';
import { CompanyProductEpic } from 'src/app/models/company_product_epic.model';
import { ProductTask } from 'src/app/models/product_task.model';
import { ApiService, PermissionsService, TinymceService, UserService, UtilsService } from 'src/app/services';
import TurndownService from 'turndown';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { InputDialogComponent, InputDialogResult } from 'src/app/components/main/shared/input-dialog/input-dialog.component';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { PRODUCT_TASK_STORY_TASK_PUBLISH_REQUIRES_PM_TOOLTIP } from 'src/app/constants/product-task-product-trio-restrictions.constants';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ProductTaskTypeImagePipe } from 'src/app/pipes/product-task-type-image.pipe';
import { ProductTaskTypeDescriptionPipe } from 'src/app/pipes/product-task-type-description.pipe';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { Editor as TinyMCEEditor } from 'tinymce';
import {
  ProductTaskDetailComponent,
  TaskNavMove,
  TaskNavMoveAbilities,
} from '../../../../product-task-detail/product-task-detail.component';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { CompanyProductTeam } from 'src/app/models/company_product_team.model';
import { ProductTaskTimeBudget } from 'src/app/models/product_task_time_budget.model';
import { HourStringToSecondsPipe } from 'src/app/pipes/hour-string-to-seconds.pipe';
import { CompanyProductPhaseTaskTimeAutocalcSetting } from 'src/app/models/company_product_phase_task_time_autocalc_setting.model';
import { CompanyProductPhaseTaskTimeAutocalcSettingsDialogComponent } from './company-product-phase-task-time-autocalc-settings-dialog/company-product-phase-task-time-autocalc-settings-dialog.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CompanyProductPhaseBoard } from 'src/app/models/company_product_phase_board.model';
import { EmployeeCalendarRegister } from 'src/app/models/employee_calendar_register.model';
import { ProductTaskBoardTimeBudget } from 'src/app/models/product_task_board_time_budget.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditCompanyProductPhaseBoardDialogComponent } from '../company-product-phase-boards/company-product-phase-board-detail/edit-company-product-phase-board-dialog/edit-company-product-phase-board-dialog.component';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompleteCompanyProductPhaseBoardDialogComponent } from '../company-product-phase-boards/company-product-phase-board-detail/complete-company-product-phase-board-dialog/complete-company-product-phase-board-dialog.component';
import { CreateProductTaskDialogComponent } from '../../../../create-product-task-dialog/create-product-task-dialog.component';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { User } from 'src/app/models/user.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { SelectorDialogComponent } from 'src/app/components/main/shared/selector-dialog/selector-dialog.component';
import { EMPTY, firstValueFrom, Observable, shareReplay, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';
import { ProductTaskBoard } from 'src/app/models/product_task_board.model';
import { RoadmapExportDialogComponent, RoadmapExportDialogData } from './roadmap-export-dialog/roadmap-export-dialog.component';
import { MultipleInputDialogComponent } from 'src/app/components/main/shared/multiple-input-dialog/multiple-input-dialog.component';
import { MultipleInputDialogData } from 'src/app/interfaces/multiple-input-dialog-data.interface';
import {
  CompanyProductPhaseUserPermissions,
  EMPTY_COMPANY_PRODUCT_PHASE_USER_PERMISSIONS,
} from 'src/app/interfaces/company-product-phase-user-permissions.interface';

@Component({
  selector: 'app-company-product-phase-backlog',
  templateUrl: './company-product-phase-backlog.component.html',
  styleUrls: ['./company-product-phase-backlog.component.css'],
  imports: [
    MatTableModule,
    MatIconModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatTooltipModule,
    ProductTaskTypeDescriptionPipe,
    ReactiveFormsModule,
    FormsModule,
    MatMenuModule,
    MatCheckboxModule,
    MatSelectModule,
    CommonModule,
    MatDividerModule,
    DragDropModule,
    CdkScrollable,
    EditorComponent,
    MatTableModule,
    RouterModule,
  ],
})
export class CompanyProductPhaseBacklogComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private utils = inject(UtilsService);
  private router = inject(Router);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  tinymce = inject(TinymceService);
  private user = inject(UserService);
  private permissions = inject(PermissionsService);
  private snack = inject(MatSnackBar);

  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('table') table: MatTable<ProductTaskCustom>;
  @ViewChildren('selectorCompanyProductEpic') selectorCompanyProductEpics: QueryList<MatSelect>;
  @ViewChildren('selectorType') selectorTypes: QueryList<MatSelect>;
  @ViewChild(EditorComponent) tinymceEditor: EditorComponent | undefined;

  private pending_description_editor_index: number | null = null;

  active_tinymce_init = this.createTinymceInit();

  objectKeysFn = Object.keys;
  now: Date = new Date();
  loading: boolean = false;
  me: User;
  tinyMceApiKey: string;

  private productTaskTypeImagePipe = inject(ProductTaskTypeImagePipe);
  private destroy_ref = inject(DestroyRef);
  private company_product_phase_id: number;
  private company_product_id: number;

  filterForm: FormGroup;
  private formPersistence: any;
  private TASKS_PER_PAGE: number = 50;
  private DAYS_PER_PAGE: number = 60;
  private last_scroll_top = 0;
  private last_scroll_left = 0;
  private text_title_new_task: string = 'Nueva tarea...';
  private hasOpenedTaskFromParams: boolean = false;
  private backlogFetchRequestId: number = 0;
  private fetchRequestSubject = new Subject<BacklogFetchRequest>();

  dataSource: MatTableDataSource<ProductTaskCustom> = new MatTableDataSource();

  displayedColumns_extra_head1: string[] = [];
  displayedColumns_extra_head2: string[] = [];
  displayedColumns_extra_head3: string[] = [];
  displayedColumns: string[] = [];
  private displayedColumnsBacklog: string[] = [
    'dragndrop',
    'company_product_epic_id',
    'type',
    'is_draft_or_archived',
    'approval',
    'code',
    'title',
    'description',
    'is_hotfix',
    'needs_testing',
    'has_dead_time',
    'time_autocalc',
    'time_total',
    'actions',
  ];
  private displayedColumnsRoadmap: string[] = ['dragndrop', 'type', 'is_draft_or_archived', 'approval', 'code', 'title', 'actions'];
  task_budgets_column_count: number = 0;

  private extracted_images: { [key: string]: string } = {};

  company_product_epics: CompanyProductEpic[] = [];
  private limit_bottom_reached: boolean = false;
  limit_right_reached: boolean = false;
  private limit_left_reached: boolean = false;

  company_product_phase_task_time_autocalc_settings: CompanyProductPhaseTaskTimeAutocalcSettingCustom[] = [];
  company_product_teams: CompanyProductTeamCustom[] = [];
  columns: (CompanyProductPhaseTaskTimeAutocalcSettingCustom | CompanyProductTeamCustom)[] = [];

  drag_disabled: boolean = true;
  is_dragging = false;
  private document_wheel_listener?: () => void;

  isClientDeveloper: boolean = false;
  isClientBusiness: boolean = false;

  private user_permissions_request$!: Observable<CompanyProductPhaseUserPermissions>;
  readonly user_permissions!: Signal<CompanyProductPhaseUserPermissions>;

  readonly storyTaskPublishRequiresPmTooltip = PRODUCT_TASK_STORY_TASK_PUBLISH_REQUIRES_PM_TOOLTIP;

  selected_task_idxs: number[] = [];
  tab_selected: 'roadmap' | 'backlog' = 'backlog';
  // Roadmap filter: when true, only show epic separator rows (hide task rows).
  show_epics_only: boolean = false;
  epic_roadmap_registered_hours: EpicRoadmapHoursByEpic = {};
  epic_title_colspan_for_epics_only: number = 1;
  epic_trailing_colspan_for_epics_only: number = 0;
  epic_row_colors: { [epicKey: string]: string } = {};
  private readonly epicRowColorPalette: string[] = ['#bfd7ff', '#bfeee8', '#c8f0cf', '#e2d4ff', '#ffe2bf', '#ffcfe5'];

  company_product: CompanyProduct | undefined;
  company_product_phase: CompanyProductPhase | undefined;

  roadmap_columns_data: { [key: string]: RoadmapColumnData } = {};
  private active_company_product_phase_board: CompanyProductPhaseBoardCustom | null = null;

  private editable_fields_in_task_detail_and_then_displayed: (keyof ProductTask)[] = [
    'title',
    'description',
    'company_product_epic',
    'company_product_epic_id',
    'is_archived',
    'is_draft',
    'is_hotfix',
    'needs_testing',
    'is_completed',
    'product_task_time_budgets',
  ];

  constructor() {
    this.company_product_phase_id = +((this.activatedRoute.snapshot.paramMap.get('phase_id') ??
      this.activatedRoute.parent?.snapshot.paramMap.get('phase_id') ??
      this.activatedRoute.parent?.parent?.snapshot.paramMap.get('phase_id')) as string);
    this.company_product_id = +((this.activatedRoute.snapshot.paramMap.get('company_product_id') ??
      this.activatedRoute.parent?.snapshot.paramMap.get('company_product_id') ??
      this.activatedRoute.parent?.parent?.snapshot.paramMap.get('company_product_id')) as string);
    this.user_permissions_request$ = this.api
      .getCompanyProductPhaseUserPermissions(this.company_product_phase_id)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.user_permissions = toSignal(this.user_permissions_request$, {
      initialValue: EMPTY_COMPANY_PRODUCT_PHASE_USER_PERMISSIONS,
    });
    this.filterForm = this.fb.group({
      from: [
        this.activatedRoute.snapshot.queryParamMap.get('from') != null
          ? dayjs(this.activatedRoute.snapshot.queryParamMap.get('from'), 'YYYY-MM-DD').startOf('day').toDate()
          : dayjs().subtract(4, 'weeks').startOf('day').toDate(),
        Validators.required,
      ],
      to: [
        this.activatedRoute.snapshot.queryParamMap.get('to') != null
          ? dayjs(this.activatedRoute.snapshot.queryParamMap.get('to'), 'YYYY-MM-DD').startOf('day').toDate()
          : dayjs().add(2, 'months').startOf('day').toDate(),
        Validators.required,
      ],
      min_order: [
        this.activatedRoute.snapshot.queryParamMap.get('min_order') != null
          ? +this.activatedRoute.snapshot.queryParamMap.get('min_order')!
          : '',
      ],
      max_order: [
        this.activatedRoute.snapshot.queryParamMap.get('max_order') != null
          ? +this.activatedRoute.snapshot.queryParamMap.get('max_order')!
          : '',
      ],
      exclude_archived: [
        this.activatedRoute.snapshot.queryParamMap.get('exclude_archived') != null
          ? +this.activatedRoute.snapshot.queryParamMap.get('exclude_archived')!
          : 0,
        Validators.required,
      ],
      epics: [
        this.activatedRoute.snapshot.queryParamMap.get('epics') != null ? +this.activatedRoute.snapshot.queryParamMap.get('epics')! : null,
      ],
      // route: [this.activatedRoute.snapshot.queryParamMap.get('route') ?? 'backlog', Validators.required]
    });
    this.destroy_ref.onDestroy((): void => this.onDragEnded());
    this.initBacklogFetchListener();
  }

  async ngOnInit() {
    this.initMe();

    await firstValueFrom(this.user_permissions_request$);
    this.checkAndOpenTaskFromParams();

    await Promise.all([
      this.fetchEpics(),
      this.fetchCompanyProductPhaseBacklogTimeAutocalcSettings(),
      this.fetchCompanyProductTeams(),
      this.fetchCompanyProduct(),
      this.fetchCompanyProductPhase(),
      this.fetchActiveCompanyProductPhaseBoard(),
    ]);

    await this.initPermissions();
    this.selectColumnsByUrl();
    this.listenRouteTabChanges();

    this.initFilterFormListener();
    this.listenQueryParameters();

    this.tinyMceApiKey = this.tinymce.getKey();
  }

  async onProductTaskCustomAttributeUpdate(
    idx: number,
    value: any,
    field: string,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    // 1. Assignem valor
    const item: any = this.dataSource.data[idx];
    let changed: boolean = true;
    let updated: boolean = false;

    if (field2 == null) {
      changed = item[field] != value;
      item[field] = value;
      if (field === 'is_draft') {
        this.setTaskCanEdit(this.dataSource.data[idx] as ProductTaskCustom);
        this.refreshTaskCanBeAddedToCurrentBoard(this.dataSource.data[idx] as ProductTaskCustom);
      }
    }

    // 2. Actualitzem via API
    if (this.dataSource.data[idx].id == null) {
      if (this.dataSource.data[idx].title !== this.text_title_new_task) {
        this.createTaskFromIdx(idx);
      }
      updated = true; // important perquè sino el dropdown de type no canvia de simbol
    } else if (field === 'company_product_phase_boards' && field2 && field4 && field5 === 'total_time_assigned_in_hours') {
      // actualitzar budget
      await this.onUpdateProductTaskBoardTimeBudget(idx, field2, field4, +value);
      updated = true;
    } else if (changed) {
      const body: ProductTask = { id: this.dataSource.data[idx].id } as ProductTask;
      (body as any)[field] = value;
      await this.api.updateProductTask(body).toPromise();
      updated = true;
    }

    // 3. Extra
    if (updated) {
      if (field === 'company_product_epic_id') {
        this.onCompanyProductEpicIdUpdated(idx);
      } else if (field === 'type') {
        this.onTypeUpdated(idx);
      } else if (field === 'description') {
        this.onDescriptionUpdated(idx);
      }

      this.cdr.markForCheck(); // actualitzem
    }
  }

  onKeydown(
    idx: number,
    evt: any,
    columnKey: string,
    field: string | undefined = undefined,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    columnKey = columnKey.replaceAll('\\', '-');
    if (evt.key === 'Enter') {
      this.onEnterPressed(idx, evt, columnKey, field, field2, field3, field4, field5);
    } else if (evt.key === 'Tab') {
      this.onTabPressed(idx, evt, columnKey, field, field2, field3, field4, field5);
    } else if (evt.key.startsWith('Arrow')) {
      this.onArrowsPressed(idx, evt, columnKey, field, field2, field3, field4, field5);
    }
  }

  onBlur(
    idx: number,
    evt: any,
    field: string | undefined = undefined,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    const evt_target = evt.target ?? evt.editorContainer;

    if (evt_target) {
      const td: HTMLTableCellElement = evt_target.closest('td');
      if (evt_target.nodeName !== 'TD') {
        const value: string = evt.target ? evt_target.value : null; // aixi no agafem valor de descripcio

        if (field) {
          const budget_column = this.columns.find(column => column.key === field);
          if (budget_column) {
            void this.updateTaskTimeCategory(idx, budget_column, Number(value || 0) * 3600).finally(() => {
              this.cdr.markForCheck();
            });
          } else if (field === 'time_autocalc' && value) {
            this.calculateTimeAutocalc(idx, value);
          } else {
            void this.onProductTaskCustomAttributeUpdate(idx, value, field, field2, field3, field4, field5);
          }
        }

        this.dataSource.data[idx].editing = false;
      }
      td.classList.remove('focused');
    }
  }

  onFocus(idx: number, evt: any) {
    const evt_target = evt.target;
    if (evt_target) {
      let td: HTMLTableCellElement | undefined;
      let input: HTMLInputElement | HTMLTextAreaElement | undefined;

      if (evt_target.nodeName === 'TD') {
        td = evt_target;
      } else {
        // està clicant a l'input
        this.dataSource.data[idx].editing = true;
        input = evt_target;
        td = evt_target.closest('td');
      }

      if (td && !td.classList.contains('focused')) {
        if (input && input.value && input.type !== 'number') {
          input.setSelectionRange(0, input.value.length);
        }

        td.classList.add('focused');
      }
    }

    const currently_editing_idx = this.dataSource.data.findIndex(task => task.editing_description);

    if (currently_editing_idx !== -1 && currently_editing_idx !== idx) {
      this.pending_description_editor_index = null;
      this.persistAndCloseDescriptionEditor(currently_editing_idx);
    }
  }

  onClickCreateNewEpic(idx: number) {
    const dialogRef = this.dialog.open(MultipleInputDialogComponent, {
      width: '500px',
      data: {
        title: 'Nuevo epic',
        description: `Introduce el título y la descripción del nuevo epic.`,
        inputs: [
          {
            slug: 'name',
            type: 'text',
            placeholder: 'Título de ejemplo...',
            label: 'Título',
            required: true,
          },
          {
            slug: 'description',
            type: 'text',
            placeholder: 'Descripción de ejemplo...',
            label: 'Descripción',
          },
        ],
        close_button_text: 'Crear epic',
      } satisfies MultipleInputDialogData,
    });

    dialogRef.afterClosed().subscribe((data: { name: string; description: string }): void => {
      if (!data.name) {
        return;
      }

      this.api
        .createCompanyProductEpic({
          name: data.name,
          description: data.description,
          company_product_id: this.company_product_id,
        } as CompanyProductEpic)
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe((data: CompanyProductEpic): void => {
          this.company_product_epics.push(data);
          this.onProductTaskCustomAttributeUpdate(idx, data.id, 'company_product_epic_id');
        });
    });
  }

  onDragStarted(): void {
    if (this.is_dragging) {
      return;
    }

    this.is_dragging = true;
    this.cdr.detectChanges();

    const handler = (event: WheelEvent) => this.handleWheelWhileDragging(event);

    document.addEventListener('wheel', handler, { passive: false, capture: true });

    this.document_wheel_listener = () => document.removeEventListener('wheel', handler, { capture: true });
  }

  onDragEnded(): void {
    this.is_dragging = false;
    this.document_wheel_listener?.();
    this.document_wheel_listener = undefined;
  }

  private handleWheelWhileDragging(event: WheelEvent): void {
    if (!this.is_dragging) {
      return;
    }

    const container = this.scrollContainerRef?.nativeElement;

    if (!container) {
      return;
    }

    container.scrollTop += event.deltaY;
    container.scrollLeft += event.deltaX;

    event.preventDefault();
  }

  onScrollTable(evt: Event) {
    const target = evt.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollLeft = target.scrollLeft;
    const scrollHeight = target.scrollHeight;
    const offsetHeight = target.offsetHeight;
    const scrollWidth = target.scrollWidth;
    const offsetWidth = target.offsetWidth;
    const isScrollingDown = scrollTop > this.last_scroll_top;
    const isScrollingUp = scrollTop < this.last_scroll_top;
    const isScrollingRight = scrollLeft > this.last_scroll_left;
    const isScrollingLeft = scrollLeft < this.last_scroll_left;
    const isAtBottom = scrollTop + offsetHeight >= scrollHeight - 1;
    const isAtTop = scrollTop === 0;
    const isAtRight = scrollLeft + offsetWidth >= scrollWidth - 1;
    const isAtLeft = scrollLeft === 0;
    this.last_scroll_top = scrollTop;
    this.last_scroll_left = scrollLeft;

    if (!this.loading) {
      if (isAtBottom && !this.limit_bottom_reached && isScrollingDown) {
        if (confirm('¿Estás seguro que quieres cargar más datos?')) {
          this.filterForm.patchValue({
            max_order: this.filterForm.value.max_order + this.TASKS_PER_PAGE,
          });
        }
      } else if (isAtTop && isScrollingUp && this.filterForm.value.min_order > 0) {
        if (confirm('¿Estás seguro que quieres cargar más datos?')) {
          this.filterForm.patchValue({
            min_order: Math.max(0, this.filterForm.value.min_order - this.TASKS_PER_PAGE),
          });
        }
      } else if (this.tab_selected === 'roadmap') {
        if (isAtRight && !this.limit_right_reached && isScrollingRight) {
          if (confirm('¿Estás seguro que quieres cargar más datos?')) {
            this.loadRoadmapFuture();
          }
        } else if (isAtLeft && !this.limit_left_reached && isScrollingLeft) {
          if (confirm('¿Estás seguro que quieres cargar más datos?')) {
            this.loadRoadmapPast();
          }
        }
      }
    }
  }

  dropped(event: CdkDragDrop<ProductTaskCustom[]>) {
    this.cdr.detach();
    this.drag_disabled = true;

    const [previousIndex, currentIndex] = this.getCurrentAndPreviousIndexOnDroppedEvent(event);

    const task_idxs_to_process: number[] = this.calcTaskIdxsToProcessAfterDropping(previousIndex, currentIndex, event);

    const body: any = this.calcPhaseOrderAfterDropping(previousIndex, currentIndex, task_idxs_to_process);

    // MOVEM DATA A LOCAL
    this.moveItemsInArray(this.dataSource, task_idxs_to_process, currentIndex);

    // DISABLE SELECT
    if (this.selected_task_idxs.length > 0) {
      this.unselectAll();
    }

    // SUBMIT
    this.cdr.reattach();
    if (Object.keys(body).length > 0) {
      this.evaluateEpicsFirstRows();
      this.table.renderRows();
      this.submitBodyToUpdateOrdersAfterDroppingAndUpdateNewOrders(body);
    }
  }

  trackById(index: number, item: ProductTaskCustom) {
    return item.id ?? `temp-${index}`;
  }

  onClickOpenTimeAutocalcSettings() {
    const dialogRef = this.dialog.open(CompanyProductPhaseTaskTimeAutocalcSettingsDialogComponent, {
      width: '500px',
      data: {
        company_product_phase_task_time_autocalc_settings: this.company_product_phase_task_time_autocalc_settings,
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.api.updateCompanyProductPhaseTaskTimeAutocalcSettingBulk(data).subscribe(data => {
          this.fetchCompanyProductPhaseBacklogTimeAutocalcSettings();
        });
      }
    });
  }

  async changeInUseAttributeFromTaskAndDistribution(
    idx: number,
    setting: CompanyProductPhaseTaskTimeAutocalcSettingCustom | CompanyProductTeamCustom
  ) {
    if (setting.type !== 'CompanyProductPhaseTaskTimeAutocalcSettingCustom') {
      return; // Es un CompanyProductTeamCustom, no un CompanyProductPhaseTaskTimeAutocalcSettingCustom
    }

    const task = this.dataSource.data[idx];
    const budgetable_key = setting.key;

    if (budgetable_key in task.budgetables) {
      const budgetable = task.budgetables[budgetable_key];

      try {
        if (task.id != null) {
          await this.removeProductTaskTimeBudget(task, budgetable_key, budgetable);
        }
        delete task.budgetables[budgetable_key];
        this.syncProductTaskTimeBudgetsFromBudgetables(task);

        let total_time_in_seconds = 0;
        Object.keys(task.budgetables).forEach((key: string) => {
          if (task.budgetables[key].in_use_for_autocalc !== false) {
            total_time_in_seconds += task.budgetables[key].total_time_budget_in_seconds;
          }
        });
        task.total_time_in_seconds = total_time_in_seconds;
        task.total_time_in_hours = this.roundWithTwoDecimals(total_time_in_seconds > 0 ? total_time_in_seconds / 3600 : 0);
      } catch {
        return;
      }
    } else {
      task.budgetables[budgetable_key] = this.createDistributableCustomFromSetting(setting);
    }

    this.calcDisabledTimeAutocalcIfAvailable(task);

    this.cdr.markForCheck();
    this.table.renderRows();
  }

  onClickCompleteBudget(idx: number, budgetableKey: string) {
    const task = this.dataSource.data[idx];
    const budgetable = task.budgetables[budgetableKey];
    if (!budgetable?.product_task_time_budget_id) return;

    if (!confirm('¿Seguro que quieres marcar esta categoría como completada?')) {
      return;
    }

    this.api.markProductTaskTimeBudgetAsCompleted(budgetable.product_task_time_budget_id).subscribe(() => {
      budgetable.completed_by_user_id = this.me!.id;
      this.syncTaskCompletedStatus(task);
      this.cdr.markForCheck();
    });
  }

  onClickUncompleteBudget(idx: number, budgetableKey: string) {
    const task = this.dataSource.data[idx];
    const budgetable = task.budgetables[budgetableKey];
    if (!budgetable?.product_task_time_budget_id) return;

    if (!confirm('¿Seguro que quieres marcar esta categoría como no completada?')) {
      return;
    }

    this.api.markProductTaskTimeBudgetAsUncompleted(budgetable.product_task_time_budget_id).subscribe(() => {
      budgetable.completed_by_user_id = null;
      this.syncTaskCompletedStatus(task);
      this.cdr.markForCheck();
    });
  }

  deleteTask(idx: number) {
    if (this.dataSource.data[idx].id > 0) {
      if (confirm('¿Estás seguro que quieres eliminar esta tarea?')) {
        this.api.deleteTask(this.dataSource.data[idx].id).subscribe(data => {
          this.deleteRowAtIdx(idx);
        });
      }
    } else {
      this.deleteRowAtIdx(idx);
    }
  }

  onClickedDescriptionParagraph(idx: number) {
    const task = this.dataSource.data[idx] as ProductTaskCustom;
    if (!task.completed && task.can_edit) {
      this.openDescriptionsTinymceAndFocus(idx);
    }
  }

  onClickOpenDescription(idx: number) {
    this.openDescriptionsTinymceAndFocus(idx);
  }

  onClickCloseDescription(idx: number) {
    this.closeDescriptionsTinymceAndFocus(idx);
  }

  onEditorInit({ editor }: { editor: TinyMCEEditor }, idx: number) {
    setTimeout(() => editor.focus(), 0);
  }

  onKeydownDescription(idx: number, e: any) {
    const isCmdOrCtrl = e.event.metaKey || e.event.ctrlKey;
    if (isCmdOrCtrl && e.event.key === 'Enter') {
      e.event.preventDefault();
      e.event.stopPropagation();
      this.closeDescriptionsTinymceAndFocus(idx);
    }
  }

  onAddNewRow(idx: number | null, position: 'below' | 'above' | null) {
    const hasReferenceTask = idx !== null;
    const new_idx = hasReferenceTask ? (position === 'below' ? idx + 1 : idx) : this.dataSource.data.length;
    const data = [...this.dataSource.data];

    const newTask = {
      id: null as any,
      order_company_product_phase: hasReferenceTask
        ? position === 'below'
          ? this.dataSource.data[idx].order_company_product_phase + 1
          : this.dataSource.data[idx].order_company_product_phase
        : this.dataSource.data.length,
      company_product_phase_id: this.company_product_phase_id,
      title: this.text_title_new_task,
      type: 'story',
      is_draft: true,
      company_product_epic_id: hasReferenceTask ? this.dataSource.data[idx].company_product_epic_id : null,
      company_product_epic: hasReferenceTask ? this.dataSource.data[idx].company_product_epic : null,
      has_dead_time: true,
      editing: true,
      is_first_epic: hasReferenceTask ? position === 'above' && this.dataSource.data[idx].is_first_epic : false,
      type_image_url: this.productTaskTypeImagePipe.transform('story'),
      is_hotfix: false,
      needs_testing: false,
      budgetables: {},
      disabled_time_autocalc: false,
      company_product_phase_boards: {},
      creator_id: this.me?.id,
      can_edit: this.user_permissions().can_create_tasks_on_backlog,

      total_time_in_seconds: 0,
      total_time_in_hours: 0,
    } as ProductTaskCustom;

    this.setTaskCanEdit(newTask);
    this.refreshTaskCanBeAddedToCurrentBoard(newTask);
    data.splice(new_idx, 0, newTask);
    this.dataSource.data = data;

    setTimeout(() => {
      const td = document.querySelector(
        `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${new_idx} td.mat-column-title input`
      ) as HTMLInputElement | undefined;
      td?.focus();
      td?.select();
    });
  }

  archiveTask(idx: number): void {
    const task = this.dataSource.data[idx];

    this.api
      .archiveTasks({ tasks_ids: [task.id], new_status_archived: true })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        task.is_archived = true;
        if (this.filterForm.value.exclude_archived) {
          this.deleteRowAtIdx(idx);
        } else {
          this.cdr.markForCheck();
        }
      });
  }

  unarchiveTask(idx: number): void {
    this.api
      .archiveTasks({ tasks_ids: [this.dataSource.data[idx].id], new_status_archived: false })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.dataSource.data[idx].is_archived = false;
        this.cdr.markForCheck();
      });
  }

  enableSelectMode() {
    if (this.displayedColumns.indexOf('select') === -1) {
      this.spliceColumns(this.displayedColumns.indexOf('dragndrop') + 1, 0, 'select');
    }
  }

  disableSelectMode() {
    const idx: number = this.displayedColumns.indexOf('select');
    if (idx > -1) {
      this.spliceColumns(idx, 1);
    }
  }

  onSelectTask(idx: number, checked: boolean) {
    this.dataSource.data[idx].selected = checked;
    if (checked) {
      if (this.selected_task_idxs.length === 0) this.enableSelectMode();
      this.selected_task_idxs.push(idx);
      this.selected_task_idxs.sort(); // important ordenar-ho per després al fer drop() que ho posi bé.
    } else {
      const selected_task_idxs_idx: number = this.selected_task_idxs.indexOf(idx);
      if (selected_task_idxs_idx > -1) this.selected_task_idxs.splice(selected_task_idxs_idx, 1);
      if (this.selected_task_idxs.length === 0) this.disableSelectMode();
    }
  }

  unselectAll() {
    this.selected_task_idxs = [];
    this.disableSelectMode();
    this.dataSource.data.forEach((task, i) => {
      this.dataSource.data[i].selected = false;
    });
  }

  enableDraft(idx: number) {
    this.onProductTaskCustomAttributeUpdate(idx, true, 'is_draft');
  }

  canPublishTask(task: Pick<ProductTaskCustom, 'type' | 'is_draft'>): boolean {
    if (!task.is_draft) {
      return false;
    }

    return this.user_permissions().can_mark_tasks_as_ready || (this.user_permissions().can_only_create_issues && task.type === 'issue');
  }

  disableDraft(idx: number) {
    const task = this.dataSource.data[idx];
    if (!this.canPublishTask(task)) {
      return;
    }

    if (this.dataSource.data[idx].requires_client_approval) {
      if (confirm('¿Estás seguro que quieres marcar esta tarea como lista para ser revisada?')) {
        this.onProductTaskCustomAttributeUpdate(idx, false, 'is_draft');
      }
    } else if (confirm('¿Estás seguro que quieres marcar esta tarea como lista para ser desarrollada?')) {
      this.onProductTaskCustomAttributeUpdate(idx, false, 'is_draft');
    }
  }

  openTask(idx: number, company_product_phase_board_id: number | string | null = 'backlog') {
    const task_id = this.dataSource.data[idx].id;
    this.openDialog(task_id, company_product_phase_board_id, idx);
  }

  async openDialog(
    task_code_id: number | string,
    company_product_phase_board_id: number | string | null = 'backlog',
    idx: number | null = null,
    comes_from_move: TaskNavMove | null = null
  ) {
    const backdropSignal$ = new Subject<void>();

    const task_nav_move_abilities: TaskNavMoveAbilities = this.getTaskNavMoveAbilitiesInBacklog(task_code_id);

    const dialog_ref = this.dialog.open(ProductTaskDetailComponent, {
      disableClose: true,
      width: '1150px',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        task_code_id: task_code_id,
        company_product_phase_board_id: company_product_phase_board_id,
        company_product_id: this.company_product_id,
        company_product_phase_id: this.company_product_phase_id,
        backdropSignal$: backdropSignal$,
        company_product_phase: this.company_product_phase,
        company_product_epics: this.company_product_epics,
        company_product_teams: this.company_product_teams,
        company_product_phase_task_time_autocalc_settings: this.company_product_phase_task_time_autocalc_settings,
        user_permissions: this.user_permissions(),
        task_nav_move_abilities,
        comes_from_move,
      },
    });

    dialog_ref.backdropClick().subscribe(() => {
      backdropSignal$.next();
    });

    const { task, task_nav_move } = await firstValueFrom(dialog_ref.afterClosed());
    if (task && idx !== null) {
      this.editTaskIfNeeded(task, idx);
    }
    if (task_nav_move) {
      const task_index = this.dataSource.data.findIndex(task => task.id === +task_code_id);
      const task_index_to_reopen = task_index + (task_nav_move === TaskNavMove.Up ? -1 : 1);
      const task_code_id_to_reopen = this.dataSource.data[task_index_to_reopen].id;
      this.openDialog(task_code_id_to_reopen, 'backlog', task_index_to_reopen, task_nav_move);
    }
  }

  clientApprove(idx: number) {
    if (confirm('¿Estás seguro que quieres aprobar esta tarea para añadirla a la hoja de ruta y empezar a desarrollarla?')) {
      this.api.approveTask(this.dataSource.data[idx].id).subscribe(data => {
        this.dataSource.data[idx].client_approval_replied_at = data.client_approval_replied_at;
        this.dataSource.data[idx].client_approval_replied_by_user_id = data.client_approval_replied_by_user_id;
        this.dataSource.data[idx].client_has_approved = data.client_has_approved;
        this.cdr.markForCheck();
      });
    }
  }

  clientDeny(idx: number) {
    if (confirm('¿Estás seguro que quieres rechazar y solicitar cambios a esta tarea?')) {
      this.api.declineTask(this.dataSource.data[idx].id).subscribe(data => {
        this.dataSource.data[idx].client_approval_replied_at = data.client_approval_replied_at;
        this.dataSource.data[idx].client_approval_replied_by_user_id = data.client_approval_replied_by_user_id;
        this.dataSource.data[idx].client_has_approved = data.client_has_approved;
        this.cdr.markForCheck();
      });
    }
  }

  askClientForApproval(idx: number) {
    if (confirm('¿Estás seguro que quieres notificar al cliente solicitando su aprobación?')) {
      this.api.requireClientApproveTask(this.dataSource.data[idx].id).subscribe(data => {
        this.dataSource.data[idx].requires_client_approval = data.requires_client_approval;
        this.dataSource.data[idx].client_approval_replied_at = data.client_approval_replied_at;
        this.dataSource.data[idx].client_approval_replied_by_user_id = data.client_approval_replied_by_user_id;
        this.dataSource.data[idx].client_has_approved = data.client_has_approved;
        this.cdr.markForCheck();
      });
    }
  }

  reaskClientForApproval(idx: number) {
    if (confirm('¿Estás seguro que quieres notificar al cliente solicitando su aprobación?')) {
      this.api.reaskApprovalTask(this.dataSource.data[idx].id).subscribe(data => {
        this.dataSource.data[idx].requires_client_approval = data.requires_client_approval;
        this.dataSource.data[idx].client_approval_replied_at = data.client_approval_replied_at;
        this.dataSource.data[idx].client_approval_replied_by_user_id = data.client_approval_replied_by_user_id;
        this.dataSource.data[idx].client_has_approved = data.client_has_approved;
        this.cdr.markForCheck();
      });
    }
  }

  cancelApprovalRequest(idx: number) {
    if (confirm('¿Estás seguro que quieres cancelar la solicitud de aprobación?')) {
      this.api.cancelApprovalTaskRequest(this.dataSource.data[idx].id).subscribe(data => {
        this.dataSource.data[idx].requires_client_approval = data.requires_client_approval;
        this.dataSource.data[idx].client_approval_replied_at = data.client_approval_replied_at;
        this.dataSource.data[idx].client_approval_replied_by_user_id = data.client_approval_replied_by_user_id;
        this.dataSource.data[idx].client_has_approved = data.client_has_approved;
        this.cdr.markForCheck();
      });
    }
  }

  loadRoadmapFuture() {
    if (this.tab_selected === 'roadmap') {
      this.filterForm.patchValue({
        to: dayjs(this.filterForm.value.to).add(this.DAYS_PER_PAGE, 'days'),
      });
    }
  }

  loadRoadmapPast() {
    this.filterForm.patchValue({
      from: dayjs(this.filterForm.value.from).subtract(this.DAYS_PER_PAGE, 'days'),
    });
  }

  openIssueFromTask(idx: number) {
    const data: ProductTaskCustom[] = [...this.dataSource.data];
    const parentTask = this.dataSource.data[idx];
    const dialogRef = this.dialog.open(CreateProductTaskDialogComponent, {
      width: '600px',
      data: {
        company_product_phase_id: this.company_product_phase_id,
        type: 'issue',
        company_product_id: this.company_product_id,
        is_draft: true,
        order_company_product_phase: data[idx].order_company_product_phase + 1,
        company_product_epic_id: parentTask.company_product_epic_id,
        is_hotfix: parentTask.is_hotfix,
        needs_testing: parentTask.needs_testing,
      },
    });

    dialogRef.afterClosed().subscribe((task): void => {
      if (task) {
        task.creator_id = this.me!.id;
        const task_custom = this.companyProductPhaseBacklogDataTaskToCustom({
          product_task: task,
          distributables: {},
          company_product_phase_boards: {},
          future_sprint_reviews: {},
          completed: false,
          has_time_registered: false,
        });

        data.forEach((ptc, foreach_idx) => {
          if (foreach_idx > idx) {
            ptc.order_company_product_phase++;
          }
        });
        data.splice(idx + 1, 0, task_custom);
        this.dataSource.data = data;

        this.evaluateEpicsFirstRows();
        this.evaluateCompanyProductPhaseMinAndMaxOrders();
        this.cdr.markForCheck();
      }
    });
  }

  async onClickCreateCompanyProductPhaseBoard(company_product_phase_board_key: string) {
    if (!this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board) {
      const has_previous_board = this.hasPreviousBoard(company_product_phase_board_key);

      const dialogRef = this.dialog.open(InputDialogComponent, {
        width: '400px',
        data: {
          title: 'Título del nuevo sprint',
          description: `Introduce el título del nuevo tablero.`,
          type: 'text',
          placeholder: 'Sprint 1...',
          label: 'Título del nuevo sprint',
          close_button_text: 'Crear tablero',
          checkbox_label: has_previous_board ? 'Propagar tareas y columnas del tablero anterior' : undefined,
          checkbox_default: true,
        },
      });

      const result: string | InputDialogResult | undefined = await dialogRef.afterClosed().toPromise();

      if (result) {
        const title = typeof result === 'string' ? result : result.value;
        const propagate_from_previous_board = typeof result === 'object' ? result.checked : false;

        const company_product_phase_board: CompanyProductPhaseBoard | undefined = await this.api
          .createCompanyProductPhaseBoard({
            company_product_phase_id: this.company_product_phase_id,
            title: title,
            start_date: this.roadmap_columns_data[company_product_phase_board_key].start_date,
            end_date: this.roadmap_columns_data[company_product_phase_board_key].end_date,
            propagate_from_previous_board,
          })
          .toPromise();
        if (company_product_phase_board) {
          this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board =
            this.companyProductPhaseBoardToCustom(company_product_phase_board);
          this.cdr.markForCheck();
          // recalcular previsions
        }
      }
    }
  }

  private hasPreviousBoard(company_product_phase_board_key: string): boolean {
    const current_column = this.roadmap_columns_data[company_product_phase_board_key];

    return Object.values(this.roadmap_columns_data).some(
      column => column.company_product_phase_board != null && column.start_date < current_column.start_date
    );
  }

  async onClickDeleteCompanyProductPhaseBoard(company_product_phase_board_key: string) {
    if (confirm('¿Estás seguro que quieres eliminar este tablero?')) {
      const response = await this.api
        .deleteCompanyProductPhaseBoard(
          this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board!.id!.toString()
        )
        .toPromise();
      if (response) {
        this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board = undefined;
        this.cdr.markForCheck();
      }
    }
  }

  async onClickModifyCompanyProductPhaseBoard(company_product_phase_board_key: string) {
    const company_product_phase_board: CompanyProductPhaseBoardCustom =
      this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board!;

    const dialogRef = this.dialog.open(EditCompanyProductPhaseBoardDialogComponent, {
      width: '800px',
      data: {
        board: company_product_phase_board,
        company_product_id: this.company_product_id,
        company_id: this.company_product!.company_id,
      },
    });

    const dataDialog: CompanyProductPhaseBoard | undefined = await dialogRef.afterClosed().toPromise();

    if (dataDialog) {
      const ppb_custom = this.companyProductPhaseBoardToCustom(
        dataDialog,
        Object.values(this.roadmap_columns_data[company_product_phase_board_key].product_task_board_time_budgets)
      );
      this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board = ppb_custom;
      this.cdr.markForCheck();
    }
  }

  async onClickCompleteCompanyProductPhaseBoard(company_product_phase_board_key: string) {
    const dialogRef = this.dialog.open(CompleteCompanyProductPhaseBoardDialogComponent, {
      data: {
        board: this.roadmap_columns_data[company_product_phase_board_key].company_product_phase_board,
      },
    });

    const dataDialog: CompanyProductPhaseBoard | undefined = await dialogRef.afterClosed().toPromise();

    if (dataDialog) {
      this.companyProductPhaseBoardToCustom(dataDialog);
      this.cdr.markForCheck();
    }
  }

  castToWorkingTimeCategory(val: unknown): WorkingTimeCategory {
    return val as WorkingTimeCategory;
  }

  /** Id del tablero activo (hoy entre start_date y end_date). Null si no hay ninguno activo. */
  get currentBoardId(): number | null {
    const from_roadmap = this.getActiveBoardIdFromColumns(Object.values(this.roadmap_columns_data));
    if (from_roadmap !== null) return from_roadmap;

    const id = this.active_company_product_phase_board?.id;
    if (id == null) return null;

    const numeric_id = Number(id);
    return Number.isNaN(numeric_id) ? null : numeric_id;
  }

  addTaskToCurrentBoard(task_idx: number): void {
    const boardId = this.currentBoardId;

    if (boardId == null) {
      this.snack.open('No se puede añadir la tarea al tablero actual porque no hay ninguno activo.', undefined, { duration: 4000 });
      return;
    }

    this.addTaskToBoard(task_idx, boardId);
  }

  addTaskToBoard(task_idx: number, board_id: number): void {
    const task = this.dataSource.data[task_idx];
    this.api
      .addTaskToBoardFromBacklog(board_id, task.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (ptb: ProductTaskBoard): void => {
          const company_product_phase_board_key = 'company_product_phase_board_' + board_id;
          const roadmap_column_key = Object.keys(this.roadmap_columns_data).find(
            k => this.roadmap_columns_data[k].company_product_phase_board?.id === board_id
          );
          const company_product_phase_board =
            (roadmap_column_key ? this.roadmap_columns_data[roadmap_column_key].company_product_phase_board : undefined) ??
            (this.active_company_product_phase_board?.id === board_id ? this.active_company_product_phase_board : undefined);
          if (company_product_phase_board) {
            task.company_product_phase_boards[company_product_phase_board_key] = {
              product_task_board_id: ptb.id,
              company_product_phase_board,
              distributables: (ptb.product_task_board_time_budgets ?? []).reduce(
                (obj: { [key: string]: DistributableCustom }, ptbtb: ProductTaskBoardTimeBudget) => {
                  const key = ptbtb.budgetable_type + '_' + ptbtb.budgetable_id;
                  obj[key] = this.productTaskBoardTimeBudgetToDistributableCustom(ptbtb);
                  return obj;
                },
                {} as { [key: string]: DistributableCustom }
              ),
            };
            if (roadmap_column_key) this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
          }
          this.refreshTaskCanBeAddedToCurrentBoard(task);
          this.cdr.markForCheck();
          this.snack.open('Tarea añadida al tablero', undefined, { duration: 3000 });
        },
        error: (err): void => {
          const msg =
            err?.status === 409 ? 'La tarea ya está en este tablero.' : (err?.error?.error ?? 'No se pudo añadir la tarea al tablero.');
          this.snack.open(msg, undefined, { duration: 4000 });
        },
      });
  }

  deleteTaskFromBoard(task_idx: number, company_product_phase_board: CompanyProductPhaseBoard, roadmap_column_key: string) {
    if (!this.roadmap_columns_data[roadmap_column_key]?.is_present_or_future) {
      this.snack.open('No se puede eliminar en tableros del pasado', undefined, { duration: 4000 });
      return;
    }
    const company_product_phase_board_key = 'company_product_phase_board_' + company_product_phase_board.id;
    const boardEntry = this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key];
    if (!boardEntry) return;
    const product_task_board_id = boardEntry.product_task_board_id;
    if (!confirm('¿Estás seguro que quieres eliminar esta tarea del tablero?')) return;

    this.api
      .deleteProductTaskBoard(product_task_board_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => {
        const task = this.dataSource.data[task_idx];
        if (roadmap_column_key in this.roadmap_columns_data) {
          const columnBudgets = this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets;
          for (const [budgetable_key, dist] of Object.entries(boardEntry.distributables)) {
            const sec = dist.total_time_assigned_in_seconds;
            const hrs = dist.total_time_assigned_in_hours;
            if (budgetable_key in columnBudgets) {
              columnBudgets[budgetable_key].total_time_assigned_in_seconds -= sec;
              columnBudgets[budgetable_key].total_time_assigned_in_hours -= hrs;
            }
            if (task.budgetables && budgetable_key in task.budgetables) {
              task.budgetables[budgetable_key].total_time_assigned_in_seconds -= sec;
              task.budgetables[budgetable_key].total_time_assigned_in_hours -= hrs;
            }
          }
          this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
        }
        delete this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key];
        this.cdr.markForCheck();
      });
  }

  deleteTaskDistributable(
    task_idx: number,
    company_product_phase_board: CompanyProductPhaseBoard,
    budgetable_key: string,
    roadmap_column_key: string
  ) {
    if (!this.roadmap_columns_data[roadmap_column_key]?.is_present_or_future) {
      this.snack.open('No se puede eliminar en tableros del pasado', undefined, { duration: 4000 });
      return;
    }
    if (confirm('¿Estás seguro que quieres eliminar esta partida del board?')) {
      const [budgetable_type, budgetable_id] = budgetable_key.split('_');
      const company_product_phase_board_key: string = 'company_product_phase_board_' + company_product_phase_board.id;
      const current_seconds: number =
        this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[budgetable_key]
          .total_time_assigned_in_seconds;
      const current_hours: number = current_seconds / 3600;
      const product_task_board_time_budget_id: number =
        this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[budgetable_key]
          .product_task_board_time_budget_id!;

      const ptbtb: DistributableCustom | undefined = Object.values(
        this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets
      ).find(
        ptbtb =>
          (ptbtb.distributable as WorkingTimeCategory).id === +budgetable_id && budgetable_type === 'App\\Models\\WorkingTimeCategory'
      );

      // actualitzar a bbdd
      this.api.deleteProductTaskBoardTimeBudget(product_task_board_time_budget_id).subscribe(data => {
        const boardWasDeletedBecauseItWasTheLastBudget = data?.board_was_deleted === true;

        if (boardWasDeletedBecauseItWasTheLastBudget) {
          delete this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key];
          // También eliminar de roadmap_columns_data si existe
          if (roadmap_column_key in this.roadmap_columns_data) {
            const boardId = company_product_phase_board.id;
            // Buscar y eliminar el board de roadmap_columns_data si existe
            const roadmapBoard = this.roadmap_columns_data[roadmap_column_key].company_product_phase_board;
            if (roadmapBoard && roadmapBoard.id === boardId) {
              // El board fue eliminado, refrescar la columna completa
              this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
            }
          }
        } else {
          // actualitzar en local
          delete this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[
            budgetable_key
          ];

          // Actualitzem COLUMN
          if (ptbtb) {
            ptbtb.total_time_assigned_in_seconds -= current_seconds;
            ptbtb.total_time_assigned_in_hours -= current_hours;

            // Actualitzem BOARD attributes
            this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
          }
        }

        // Actualtizem ROW
        this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_hours -= current_hours;
        this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_seconds -= current_seconds;

        this.cdr.markForCheck();
      });
    }
  }

  async switchTaskToNewPhase(task_idx: number) {
    const phases: ArrayResponse<CompanyProductPhase> | undefined = await this.api
      .getCompanyProductPhases({ company_product_id: this.company_product_id, is_active: true })
      .toPromise();
    if (phases) {
      const phases_serialized = phases.data
        .filter(p => p.id != this.company_product_phase_id)
        .map(p => {
          return {
            value: p.id,
            textToShow: p.company_product_phase_type.name,
          };
        });

      if (phases_serialized.length > 0) {
        const dialogRef = this.dialog.open(SelectorDialogComponent, {
          width: '400px',
          data: {
            dialog_title: 'Seleccionar fase',
            dialog_ok_button: 'Mover a esta fase',
            dialog_cancel_button: 'Cancelar',
            data: phases_serialized,
          },
        });

        dialogRef.afterClosed().subscribe(company_product_phase_id => {
          if (company_product_phase_id) {
            this.loading = true;
            this.api.moveTaskToPhase(this.dataSource.data[task_idx].id, company_product_phase_id).subscribe(
              data => {
                this.dataSource.data.forEach((item, foreach_idx) => {
                  if (foreach_idx > task_idx) {
                    item.order_company_product_phase -= 1;
                  }
                });

                this.dataSource.data.splice(task_idx, 1); // eliminem

                this.evaluateEpicsFirstRows();
                this.evaluateCompanyProductPhaseMinAndMaxOrders();
                this.cdr.markForCheck();
              },
              () => {
                this.loading = false;
              }
            );
          }
        });
      } else this.snack.open('No se han encontrados fases disponibles.', 'Ok', { duration: 3000 });
    }
  }

  startManualReorderingForTask(task_idx: number) {
    const product_task_custom: ProductTaskCustom = this.dataSource.data[task_idx];

    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '400px',
      data: {
        title: 'Mover tarea',
        description: `Introduce el código de la tarea de referencia de esta fase a donde quieres moverla. Tu tarea se moverá a la posición de esa tarea.`,
        type: 'text',
        placeholder: (this.company_product?.code ?? 'BP') + 'XXX...',
        label: 'Código de la tarea',
        close_button_text: 'Seleccionar tarea',
      },
    });

    dialogRef.afterClosed().subscribe((code: string) => {
      if (code) {
        this.api.getTaskByCode(code).subscribe(
          data => {
            if (data.company_product_phase_id != this.company_product_phase_id) {
              this.snack.open('No puede mover hacia una tarea de otra fase.');
            } else {
              this.loading = true;
              const body: { [key: number]: number } = {};
              body[product_task_custom.id] = data.order_company_product_phase;
              this.api.changePhaseTasksOrder(this.company_product_phase_id, body).subscribe(
                () => {
                  this.reload();
                },
                () => {
                  this.loading = false;
                }
              );
            }
          },
          () => {
            this.snack.open('No se ha encontrado una tarea con el código ' + code + '.');
          }
        );
      }
    });
  }

  /**
   * Calcula el valor máximo entre 0 y la diferencia entre el presupuesto total y el tiempo asignado o hecho
   * @param budgetable - El objeto budgetable con las propiedades de tiempo
   * @returns El valor máximo calculado
   */
  calculateMaxTimeValueRemainingToAssignInBoard(budgetable: any): number {
    const totalBudget = budgetable.total_time_budget_in_hours;
    const totalAssigned = budgetable.total_time_assigned_in_hours;
    const totalDone = budgetable.total_time_done_in_hours;

    return Math.max(0, totalBudget - totalAssigned, totalBudget - totalDone);
  }

  private reload() {
    this.dataSource.data = [];
    this.roadmap_columns_data = {};
    this.onNewParamsTrigger(this.activatedRoute.snapshot.queryParams);
  }

  private initFilterFormListener() {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(data => {
      this.show_epics_only = +data.epics === 1;
      if (this.formPersistence == null || JSON.stringify(this.formPersistence) != JSON.stringify(data)) {
        const params = this.utils.cloneObj(data);
        if (params.from) params.from = dayjs(params.from).format('YYYY-MM-DD');
        if (params.to) params.to = dayjs(params.to).format('YYYY-MM-DD');

        this.router.navigate([], { queryParams: params });
      }
    });
  }

  private listenQueryParameters() {
    this.activatedRoute.queryParams
      .pipe(
        map(params => this.normalizeRouteParams(params)),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(params_temp => {
        if (this.formPersistence == null || JSON.stringify(params_temp) !== JSON.stringify(this.formPersistence)) {
          this.onNewParamsTrigger(params_temp, this.formPersistence);
        }
      });
  }

  private normalizeRouteParams(params: Record<string, unknown>): Record<string, unknown> {
    const params_temp = this.utils.cloneObj(params);

    Object.keys(params_temp).forEach(param_key => {
      if (params_temp[param_key] != null && params_temp[param_key] != '' && !isNaN(+params_temp[param_key])) {
        params_temp[param_key] = +params_temp[param_key];
      } else if (params_temp[param_key] != null && (params_temp[param_key] as string).indexOf('-') > -1) {
        params_temp[param_key] = dayjs(params_temp[param_key] as string, 'YYYY-MM-DD').toDate();
      }
    });

    if (params_temp.from == null) {
      params_temp.from = dayjs().startOf('day').subtract(4, 'weeks').toDate();
    }

    if (params_temp.to == null) {
      params_temp.to = dayjs().startOf('day').add(2, 'months').toDate();
    }

    return params_temp;
  }

  private initBacklogFetchListener(): void {
    this.fetchRequestSubject
      .pipe(
        switchMap(({ params, query, request_id }) =>
          this.api.getCompanyProductPhaseBacklog(this.company_product_phase_id, query as any).pipe(
            map(
              (data): BacklogFetchResult => ({
                data,
                prepend_or_append: params.prepend_or_append,
                request_id,
              })
            ),
            catchError((): typeof EMPTY => EMPTY),
            finalize((): void => {
              if (request_id === this.backlogFetchRequestId) {
                this.loading = false;
                this.cdr.markForCheck();
              }
            })
          )
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((result: BacklogFetchResult | undefined): void => {
        if (result) {
          this.onNewDataFetched(result.data, result.prepend_or_append);
        }
      });
  }

  private onNewParamsTrigger(params_temp: Record<string, unknown>, old_params: Record<string, unknown> | undefined = undefined) {
    if (!(params_temp.from instanceof Date)) {
      params_temp = this.normalizeRouteParams(params_temp);
    }

    this.filterForm.patchValue(params_temp, { emitEvent: false });
    this.show_epics_only = +(params_temp.epics ?? 0) === 1;
    const fetch_params: BacklogFetchQueryParams = this.calcFetchParams(old_params, params_temp);
    this.formPersistence = { ...params_temp };

    this.fetch(fetch_params);
  }

  private calcFetchParams(old_params: any, params: any): BacklogFetchQueryParams {
    let res: BacklogFetchQueryParams = {} as any;

    if (old_params) {
      if (old_params.min_order != null && params.min_order != null && params.min_order < old_params.min_order) {
        // ENS ESTEM MOVENT VERTICALMENT AMUNT
        res.prepend_or_append = 'v_prepend';
        res.min_order = +params.min_order;
        res.max_order = +old_params.min_order - 1;
        res.from = params.from;
        res.to = params.to;
      } else if (old_params.min_order != null && params.min_order != null && params.max_order > old_params.max_order) {
        // ENS ESTEM MOVENT VERTICALMENT AVALL
        res.prepend_or_append = 'v_append';
        res.min_order = +old_params.max_order + 1;
        res.max_order = +params.max_order;
        res.from = params.from;
        res.to = params.to;
      } else if (params.from < old_params.from) {
        // ENS ESTEM MOVENT HORITZONTALMENT A L'ESQUERRA
        res.prepend_or_append = 'h_prepend';
        res.min_order = params.min_order;
        res.max_order = params.max_order;
        res.from = params.from;
        res.to = dayjs(old_params.from).subtract(1, 'day').toDate();
      } else if (params.to > old_params.to) {
        // ENS ESTEM MOVENT HORITZONTALMENT A LA DRETA
        res.prepend_or_append = 'h_append';
        res.min_order = old_params.min_order;
        res.max_order = params.max_order;
        res.from = dayjs(old_params.to).add(1, 'day').toDate();
        res.to = params.to;
      } else {
        console.error('ERROR CALC_FETCH_PARAMS', 'old_params', old_params, 'params', params);
        throw new Error('How is this even possible?');
      }
    } else {
      res = {
        prepend_or_append: 'first_time',
        min_order: params.min_order ?? undefined,
        max_order: params.max_order ?? undefined,
        from: params.from,
        to: params.to,
      };
    }

    return res;
  }

  private fetch(params: BacklogFetchQueryParams) {
    this.loading = true;
    const query = this.buildBacklogApiQueryParams(params);
    const request_id = ++this.backlogFetchRequestId;
    this.fetchRequestSubject.next({ params, query, request_id });
  }

  private buildBacklogApiQueryParams(params: BacklogFetchQueryParams): Record<string, unknown> {
    const query: Record<string, unknown> = {
      ...this.filterForm.value,
      ...params,
    };

    if (this.isRoadmapView()) {
      query.roadmap = 1;
      query.totals =
        this.dataSource.data.length === 0
          ? 'all'
          : params.prepend_or_append.startsWith('h_')
            ? 'boards'
            : params.prepend_or_append.startsWith('v_')
              ? 'tasks'
              : 'all';
      query.prediction_state = JSON.stringify(this.buildPredictionState());
    } else {
      query.roadmap = 0;
      delete query.totals;
      delete query.prediction_state;
    }

    delete query.prepend_or_append;
    delete query.epics;
    query.from = this.utils.dateToStringYYYYMMDD(query.from as Date);
    query.to = this.utils.dateToStringYYYYMMDD(query.to as Date);

    if (query.min_order === undefined || query.max_order === undefined) {
      delete query.min_order;
      delete query.max_order;
    }

    return query;
  }

  private isRoadmapView(): boolean {
    return this.activatedRoute.routeConfig?.path === 'roadmap';
  }

  private listenRouteTabChanges(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((): void => {
        const next_tab: 'roadmap' | 'backlog' = this.isRoadmapView() ? 'roadmap' : 'backlog';

        // Solo query params (p.ej. loadRoadmapPast/Future): no resetear columnas del roadmap.
        if (next_tab === this.tab_selected) {
          return;
        }

        this.selectColumnsByUrl();

        if (this.isRoadmapView() && Object.keys(this.roadmap_columns_data).length === 0) {
          this.reload();
        }
      });
  }

  private turndownServiceForDescription() {
    const turndownService = new TurndownService({
      br: '  \n',
      headingStyle: 'atx',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
    });
    turndownService.addRule('ignoreBase64Images', {
      filter: (node: any) => node.nodeName === 'IMG' && (node as HTMLImageElement).src.startsWith('data:image'),
      replacement: (content, node) => {
        const imageIndex = Object.keys(this.extracted_images).length;
        const filename = `image_${imageIndex}.jpg`;
        this.extracted_images[filename] = (node as HTMLImageElement).src;
        return `![${filename}]()`;
      },
    });
    turndownService.addRule('lineBreak', {
      filter: ['br'],
      replacement: () => '  \n', // Markdown: dos espacios + \n para salto de línea real
    });
    return turndownService;
  }

  private onNewDataFetched(data: CompanyProductPhaseBacklogData, prepend_or_append: PrependOrAppendEnum) {
    const company_product_phase_boards = data.company_product_phase_boards ?? [];
    const future_sprint_reviews = data.future_sprint_reviews ?? [];

    if (data.product_tasks.length) this.onNewTasksFetched(data, prepend_or_append as 'v_append' | 'v_prepend' | 'first_time');
    if (
      (company_product_phase_boards.length || future_sprint_reviews.length) &&
      (this.tab_selected === 'roadmap' || Object.keys(this.roadmap_columns_data).length === 0)
    )
      this.onRoadmapColumnsFetched(
        { ...data, company_product_phase_boards, future_sprint_reviews },
        prepend_or_append as 'h_append' | 'h_prepend' | 'first_time'
      );
    this.cdr.markForCheck(); // actualitzem

    if (data.product_tasks.length === 0 && ['first_time', 'v_append'].includes(prepend_or_append)) {
      this.limit_bottom_reached = true;
    } else if (
      company_product_phase_boards.length === 0 &&
      future_sprint_reviews.length === 0 &&
      (prepend_or_append === 'first_time' || prepend_or_append.startsWith('h_'))
    ) {
      if (prepend_or_append === 'first_time') {
        this.limit_left_reached = true;
        this.limit_right_reached = true;
      } else if (prepend_or_append === 'h_append') {
        this.limit_right_reached = true;
      } else if (prepend_or_append === 'h_prepend') {
        this.limit_left_reached = true;
      }
    }
  }

  private onNewTasksFetched(
    data: CompanyProductPhaseBacklogData,
    prepend_or_append: 'v_append' | 'v_prepend' | 'h_append' | 'h_prepend' | 'first_time'
  ) {
    const hadZeroItems = this.dataSource.data.length === 0;

    const new_data: ProductTaskCustom[] = data.product_tasks.map(item => this.companyProductPhaseBacklogDataTaskToCustom(item));

    const scrollContainer = this.scrollContainerRef?.nativeElement;
    let previousScrollHeight = 0;
    let previousScrollTop = 0;

    if (prepend_or_append === 'v_prepend' && scrollContainer) {
      previousScrollHeight = scrollContainer.scrollHeight;
      previousScrollTop = scrollContainer.scrollTop;
    }

    if (prepend_or_append === 'h_prepend' || prepend_or_append === 'h_append') {
      // Horizontal roadmap pagination returns same tasks enriched with extra sprint columns.
      // Merge those task-level board/future-sprint values into existing datasource tasks.
      const currentById = new Map(this.dataSource.data.filter(t => t.id != null).map(task => [task.id as number, task]));
      new_data.forEach(incomingTask => {
        if (incomingTask.id == null) return;
        const existingTask = currentById.get(incomingTask.id);
        if (!existingTask) return;

        existingTask.company_product_phase_boards = {
          ...existingTask.company_product_phase_boards,
          ...incomingTask.company_product_phase_boards,
        };

        existingTask.future_sprint_reviews = {
          ...(existingTask.future_sprint_reviews ?? {}),
          ...(incomingTask.future_sprint_reviews ?? {}),
        };
      });
    } else if (prepend_or_append === 'v_prepend') {
      this.dataSource.data = [...new_data, ...this.dataSource.data];
    } else if (prepend_or_append === 'v_append' || prepend_or_append === 'first_time') {
      this.dataSource.data = [...this.dataSource.data, ...new_data];
    }

    this.refreshAllTasksCanBeAddedToCurrentBoard();

    setTimeout(() => {
      if (!scrollContainer) return;

      setTimeout(() => {
        if (hadZeroItems) {
          // seleccionem la primera
          if (this.tab_selected === 'backlog') {
            this.selectorCompanyProductEpics.toArray()[0]?._elementRef.nativeElement.closest('td')?.focus();
          } else {
            const query: string =
              '.bisual-table-container .mat-mdc-table tbody tr:not(.epic-separator-row,.add-new-task-row) td.active-this-week';
            (document.querySelector(query) as HTMLTableCellElement)?.focus();
          }
        }
      }, 500);

      if (prepend_or_append === 'v_prepend') {
        const newScrollHeight = scrollContainer.scrollHeight;
        scrollContainer.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop;

        // Pequeño scroll hacia arriba como indicativo
        setTimeout(() => {
          scrollContainer.scrollBy({
            top: -200,
            behavior: 'smooth',
          });
        }, 100);
      } else if (prepend_or_append === 'v_append') {
        const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 100;

        if (isNearBottom) {
          // Scroll hacia abajo suave para ver lo nuevo
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    });

    this.evaluateEpicsFirstRows();
    this.evaluateCompanyProductPhaseMinAndMaxOrders();
    this.recalculateEpicRoadmapRegisteredHours();
  }

  private onRoadmapColumnsFetched(data: CompanyProductPhaseBacklogData, prepend_or_append: 'h_append' | 'h_prepend' | 'first_time') {
    const roadmap_col_keys: string[] = [];
    const company_product_phase_boards = data.company_product_phase_boards ?? [];
    const future_sprint_reviews = data.future_sprint_reviews ?? [];

    // 1. COMPANY_PRODUCT PAHSE BOARDS
    company_product_phase_boards.forEach(board_item => {
      const key: string = 'roadmap_' + dayjs(board_item.company_product_phase_board.start_date).format('YYYY_MM_DD');

      if (!(key in this.roadmap_columns_data)) {
        const custom_company_product_phase_board = this.companyProductPhaseBoardToCustom(
          board_item.company_product_phase_board,
          Object.values(board_item.distributables)
        );
        this.roadmap_columns_data[key] = {
          company_product_phase_board: custom_company_product_phase_board,
          employee_calendar_register: undefined,
          start_date: new Date(board_item.company_product_phase_board.start_date!),
          end_date: new Date(board_item.company_product_phase_board.end_date!),
          product_task_board_time_budgets: Object.fromEntries(
            Object.entries(
              this.distributablesToDistributableCustom(
                board_item.distributables,
                custom_company_product_phase_board,
                board_item.distributables
              )
            ).filter(([key, value]) => (value.distributable as WorkingTimeCategory).slug !== 'review-task')
          ),
          active_this_week: false,
          is_present_or_future: false,
          weeks: board_item.weeks,
        };
        this.roadmap_columns_data[key].active_this_week =
          this.roadmap_columns_data[key].start_date <= this.now && this.now <= this.roadmap_columns_data[key].end_date;

        this.roadmap_columns_data[key].is_present_or_future =
          this.roadmap_columns_data[key].start_date >= this.now || this.roadmap_columns_data[key].active_this_week;

        roadmap_col_keys.push(
          ...Object.keys(board_item.distributables)
            .filter(
              distributable_key =>
                (board_item.distributables[distributable_key].distributable as WorkingTimeCategory).slug !== 'review-task'
            )
            .sort((key1: string, key2: string) => this.sortDistributableKey(key1, key2))
            .map(distributable_key => `${key}_${distributable_key}`)
        );
      }
    });

    // 2. FUTURE SPRINTS
    future_sprint_reviews.forEach(ecr_item => {
      const key: string = 'roadmap_' + dayjs(ecr_item.start_date).format('YYYY_MM_DD');

      if (!(key in this.roadmap_columns_data)) {
        this.roadmap_columns_data[key] = {
          company_product_phase_board: undefined,
          employee_calendar_register: ecr_item.employee_calendar_register,
          start_date: new Date(ecr_item.start_date),
          end_date: new Date(ecr_item.end_date),
          product_task_board_time_budgets: Object.fromEntries(
            Object.entries(this.distributablesToDistributableCustom(ecr_item.distributables, undefined, ecr_item.distributables)).filter(
              ([key, value]) => (value.distributable as WorkingTimeCategory).slug !== 'review-task'
            )
          ),
          active_this_week: false,
          is_present_or_future: false,
          weeks: ecr_item.weeks,
        };
        this.roadmap_columns_data[key].active_this_week =
          this.roadmap_columns_data[key].start_date <= this.now && this.now <= this.roadmap_columns_data[key].end_date;

        this.roadmap_columns_data[key].is_present_or_future =
          this.roadmap_columns_data[key].start_date >= this.now || this.roadmap_columns_data[key].active_this_week;
        roadmap_col_keys.push(
          ...Object.keys(ecr_item.distributables)
            .filter(
              distributable_key => (ecr_item.distributables[distributable_key].distributable as WorkingTimeCategory).slug !== 'review-task'
            )
            .sort((key1: string, key2: string) => this.sortDistributableKey(key1, key2))
            .map(distributable_key => `${key}_${distributable_key}`)
        );
      }
    });

    // 3. AFEGIM COLUMNES (solo si estamos en roadmap, si estamos en backlog solo guardamos los datos)
    if (this.tab_selected === 'roadmap' && roadmap_col_keys.length > 0) {
      this.spliceColumns(
        ['h_append', 'first_time'].includes(prepend_or_append)
          ? this.displayedColumns.indexOf('actions')
          : this.displayedColumns.indexOf(this.displayedColumns.find(col => col.startsWith('roadmap_'))!),
        0,
        ...roadmap_col_keys
      );
    }

    this.recalculateEpicRoadmapRegisteredHours();
    this.refreshAllTasksCanBeAddedToCurrentBoard();
  }

  private showBacklog() {
    this.tab_selected = 'backlog';

    // borrem tot i inserim nou
    this.spliceColumns(0, this.displayedColumns.length, ...this.displayedColumnsBacklog);
    if (Object.keys(this.company_product_phase_task_time_autocalc_settings).length) {
      this.addTaskBudgetsColumns();
    }
    this.refreshAllTasksCanBeAddedToCurrentBoard();
  }

  private showRoadmap() {
    this.tab_selected = 'roadmap';

    // borrem tot i inserim nou
    this.spliceColumns(0, this.displayedColumns.length, ...this.displayedColumnsRoadmap);
    if (Object.keys(this.company_product_phase_task_time_autocalc_settings).length) {
      this.addTaskBudgetsColumns();
    }
    this.refreshAllTasksCanBeAddedToCurrentBoard();

    //   // Si ya tenemos datos de roadmap cargados, añadimos las columnas
    //   if (Object.keys(this.roadmap_columns_data).length > 0) {
    //     this.addRoadmapColumnsFromExistingData();
    //   }
  }

  // private addRoadmapColumnsFromExistingData() {
  //   const roadmap_col_keys: string[] = [];

  //   // Iterar sobre roadmap_columns_data existente y construir las keys de columnas
  //   Object.keys(this.roadmap_columns_data)
  //     .sort((key1: string, key2: string) => {
  //       const date1 = this.roadmap_columns_data[key1].start_date;
  //       const date2 = this.roadmap_columns_data[key2].start_date;
  //       return date1.getTime() - date2.getTime();
  //     })
  //     .forEach(key => {
  //       const columnData = this.roadmap_columns_data[key];
  //       roadmap_col_keys.push(
  //         ...Object.keys(columnData.product_task_board_time_budgets)
  //           .sort((key1: string, key2: string) => this.sortDistributableKey(key1, key2))
  //           .map(distributable_key => `${key}_${distributable_key}`)
  //       );
  //     });

  //   // Añadir las columnas antes de 'actions'
  //   if (roadmap_col_keys.length > 0) {
  //     const actionsIndex = this.displayedColumns.indexOf('actions');
  //     if (actionsIndex >= 0) {
  //       this.spliceColumns(actionsIndex, 0, ...roadmap_col_keys);
  //     }
  //   }
  // }

  private async fetchCompanyProductTeams() {
    const data = await this.api.getCompanyProductTeamFromCompanyProduct(this.company_product_id.toString()).toPromise();
    if (data) {
      this.company_product_teams = data.map((item: CompanyProductTeam) => {
        const color: string = this.stringToColor(item.name);
        return {
          ...item,
          color: color,
          key: 'App\\Models\\CompanyProductTeam_' + item.id,
          background_soft_color: this.getSecondaryColor(color),
          progress_bar_color: this.getSecondaryColor(color, 0.4),
          icon: item.google_fonts_icon_slug || 'help',
          left: undefined,
          company_product_phase_task_time_autocalc_setting_category: undefined,
          type: 'CompanyProductTeamCustom',
        };
      });
    }
  }

  private stringToColor(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      // Simple hash basado en el código de caracteres
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (let i = 0; i < 3; i++) {
      // Extrae los bytes y convierte a hexadecimal
      const value = (hash >> (i * 8)) & 0xff;
      color += ('00' + value.toString(16)).slice(-2);
    }

    return color;
  }

  private sortDistributableKey(key1: string, key2: string) {
    const [is_company_product_team_1, is_working_time_category_1, is_dev_1, is_uxui_1] = this.sortDistributableKeyOptions(key1);
    const [is_company_product_team_2, is_working_time_category_2, is_dev_2, is_uxui_2] = this.sortDistributableKeyOptions(key2);

    const rank = ([is_company_product_team, is_working_time_category, is_dev, is_uxui]: boolean[]) => {
      if (is_dev) return 0;
      if (is_uxui) return 1;
      if (is_working_time_category) return 2;
      if (is_company_product_team) return 3;
      return 4; // otros
    };

    const rank1 = rank([is_company_product_team_1, is_working_time_category_1, is_dev_1, is_uxui_1]);
    const rank2 = rank([is_company_product_team_2, is_working_time_category_2, is_dev_2, is_uxui_2]);

    return rank1 - rank2;
  }

  /**
   *
   * @param distributable_key
   * @returns [ is_company_product_team, is_working_time_category, is_dev, is_uxui ]
   */
  private sortDistributableKeyOptions(distributable_key: string): boolean[] {
    const [distributable_type, distributable_id] = distributable_key.split('_');
    const is_company_product_team = distributable_type === 'App\\Models\\CompanyProductTeam';
    const is_working_time_category = !is_company_product_team;
    const is_dev =
      is_working_time_category &&
      this.company_product_phase_task_time_autocalc_settings.find(
        s =>
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category != null &&
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category.id === +distributable_id
      )!.company_product_phase_task_time_autocalc_setting_category.slug === 'task-time-tracking';
    const is_uxui =
      is_working_time_category &&
      this.company_product_phase_task_time_autocalc_settings.find(
        s =>
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category != null &&
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category.id === +distributable_id
      )!.company_product_phase_task_time_autocalc_setting_category.slug === 'product-design';
    return [is_company_product_team, is_working_time_category, is_dev, is_uxui];
  }

  private async fetchCompanyProductPhaseBacklogTimeAutocalcSettings() {
    const data = await this.api
      .getCompanyProductPhaseTaskTimeAutocalcSettings({ company_product_phase_id: this.company_product_phase_id })
      .toPromise();
    if (data) {
      this.company_product_phase_task_time_autocalc_settings = data.data.map((item: CompanyProductPhaseTaskTimeAutocalcSetting) => {
        const color: string = item.company_product_phase_task_time_autocalc_setting_category.working_time_category?.color || '#000000';
        return {
          ...item,
          key:
            item.company_product_phase_task_time_autocalc_setting_category.working_time_category != null
              ? 'App\\Models\\WorkingTimeCategory_' +
                item.company_product_phase_task_time_autocalc_setting_category.working_time_category.id
              : item.company_product_phase_task_time_autocalc_setting_category.slug,
          color: color,
          background_soft_color: this.getSecondaryColor(color),
          progress_bar_color: this.getSecondaryColor(color, 0.4),
          icon: item.company_product_phase_task_time_autocalc_setting_category.working_time_category?.google_fonts_icon_slug || 'help',
          left: '', // es calcula després
          name: item.company_product_phase_task_time_autocalc_setting_category.name,
          type: 'CompanyProductPhaseTaskTimeAutocalcSettingCustom',
        };
      });
    }
  }

  private async fetchEpics() {
    const data = await this.api.getCompanyProductEpics(this.company_product_phase_id).toPromise();
    if (data) this.company_product_epics = data;
  }

  private onEnterPressed(
    idx: number,
    evt: any,
    columnKey: string,
    field: string | undefined = undefined,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    if (evt.target) {
      const input: HTMLInputElement | HTMLTextAreaElement | undefined =
        evt.target instanceof HTMLInputElement || evt.target instanceof HTMLTextAreaElement
          ? evt.target
          : ((evt.target as HTMLElement).querySelector(':scope > input') ??
            (evt.target as HTMLElement).querySelector('textarea') ??
            (evt.target as HTMLElement).querySelector(':scope > div > input'));
      const td: HTMLTableCellElement = evt.target instanceof HTMLTableCellElement ? evt.target : evt.target.closest('td');
      const evtTargetHasCheckbox: boolean =
        evt.target instanceof HTMLTableCellElement && !!evt.target.querySelector(':scope > mat-checkbox');
      const evtTargetHasDropdown: boolean = evt.target instanceof HTMLTableCellElement && !!evt.target.querySelector(':scope > mat-select');
      if (field === 'description') {
        if (!this.dataSource.data[idx].completed && !this.dataSource.data[idx].editing_description) {
          this.openDescriptionsTinymceAndFocus(idx);
        }
      } else if (input) {
        if (this.dataSource.data[idx].editing && !this.dataSource.data[idx].completed) {
          if (field === 'time_autocalc') {
            this.calculateTimeAutocalc(idx, input.value);
          }

          const s = this.columns.find(s => s.key === field);
          if (s) {
            this.updateTaskTimeCategory(idx, s, Number(input!.value) * 3600);
          }

          input.blur(); // unfocus --> guardar canvis

          if (field === 'title' && this.dataSource.data[idx].id == null && (evt.metaKey || evt.ctrlKey)) {
            this.onAddNewRow(idx, 'below');
          } else {
            const next_td = this.findNextElementWithQuery(td, `.focusable.mat-column-${columnKey}`);
            next_td?.focus();
          }
        } else {
          if (document.activeElement != input) {
            input.focus();
          }
        }
      } else if (evtTargetHasCheckbox && field && !this.dataSource.data[idx].completed) {
        this.onProductTaskCustomAttributeUpdate(idx, !(this.dataSource.data[idx] as any)[field], field, field2, field3, field4, field5); // toggle
      } else if (evtTargetHasDropdown) {
        if (field === 'company_product_epic_id') {
          this.selectorCompanyProductEpics.toArray()[idx].open();
          this.selectorCompanyProductEpics.toArray()[idx].focus();
        } else if (field === 'type') {
          this.selectorTypes.toArray()[idx].open();
          this.selectorTypes.toArray()[idx].focus();
        }
      }
    }
  }

  private onTabPressed(
    idx: number,
    evt: any,
    columnKey: string,
    field: string | undefined = undefined,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    if (evt.target) {
      if (!this.dataSource.data[idx].editing) {
        evt.preventDefault();
        evt.stopPropagation();
        const td = evt.shiftKey
          ? this.findPreviousElementWithQuery(evt.target, '.focusable')
          : this.findNextElementWithQuery(evt.target, '.focusable');
        td?.focus(); // --> farà blur() --> guardar canvis
      }
    }
  }

  private onArrowsPressed(
    idx: number,
    evt: any,
    columnKey: string,
    field: string | undefined = undefined,
    field2: string | undefined = undefined,
    field3: string | undefined = undefined,
    field4: string | undefined = undefined,
    field5: string | undefined = undefined
  ) {
    if (evt.target) {
      if (!evt.shiftKey && this.selected_task_idxs.length > 0) {
        this.unselectAll();
      }

      if (!this.dataSource.data[idx].editing) {
        evt.preventDefault();
        evt.stopPropagation();
        if (evt.key === 'ArrowDown') {
          if (this.dataSource.data.length > idx) {
            if (evt.shiftKey) {
              if (this.selected_task_idxs.length == 0 || !this.dataSource.data[idx].selected) {
                // seleccionem aquesta
                this.onSelectTask(idx, true);
              }
              if (this.dataSource.data.length > idx + 1) {
                // seleccionem la següent
                this.onSelectTask(idx + 1, true);
              }
            }

            setTimeout(() => {
              let target = evt.target;
              if (!target.isConnected)
                target = document.querySelector(
                  `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${idx} td.mat-column-${columnKey}`
                );
              const td = this.findNextElementWithQuery(target, `.mat-column-${columnKey}`)?.closest('td');
              td?.focus();
            });
          }
        } else if (evt.key === 'ArrowUp') {
          if (idx > 0) {
            if (evt.shiftKey) {
              if (this.selected_task_idxs.length == 0 || !this.dataSource.data[idx].selected) {
                // seleccionem aquesta
                this.onSelectTask(idx, true);
              }
              if (idx - 1 >= 0) {
                // seleccionem la següent
                this.onSelectTask(idx - 1, true);
              }
            }

            setTimeout(() => {
              let target = evt.target;
              if (!target.isConnected)
                target = document.querySelector(
                  `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${idx} td.mat-column-${columnKey}`
                );
              const td = this.findPreviousElementWithQuery(target, `.mat-column-${columnKey}`)?.closest('td');
              td?.focus();
            });
          }
        } else if (evt.key === 'ArrowLeft') {
          setTimeout(() => {
            let target = evt.target;
            if (!target.isConnected)
              target = document.querySelector(
                `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${idx} td.mat-column-${columnKey}`
              );
            const td = this.findPreviousElementWithQuery(target, `.focusable`)?.closest('td');
            td?.focus();
          });
        } else if (evt.key === 'ArrowRight') {
          setTimeout(() => {
            let target = evt.target;
            if (!target.isConnected)
              target = document.querySelector(
                `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${idx} td.mat-column-${columnKey}`
              );
            const td = this.findNextElementWithQuery(target, `.focusable`)?.closest('td');
            td?.focus();
          });
        }
      }
    }
  }

  private findNextElementWithQuery(element: HTMLElement, querySelector: string): HTMLElement | null {
    const all = Array.from(document.querySelectorAll(querySelector));
    const index = all.indexOf(element);
    return index >= 0 && index < all.length - 1 ? (all as any)[index + 1] : null;
  }

  private findPreviousElementWithQuery(element: HTMLElement, querySelector: string): HTMLElement | null {
    const all = Array.from(document.querySelectorAll(querySelector));
    const index = all.indexOf(element);
    return index > 0 ? (all as any)[index - 1] : null;
  }

  private onCompanyProductEpicIdUpdated(idx: number) {
    const value: number = this.dataSource.data[idx].company_product_epic_id!;
    this.dataSource.data[idx].company_product_epic = this.company_product_epics.find(epic => epic.id === value);
    this.evaluateEpicsFirstRows();
    const td = this.selectorCompanyProductEpics.toArray()[idx]._elementRef.nativeElement.closest('td');

    setTimeout(() => {
      td?.focus();
    });
  }

  private onTypeUpdated(idx: number) {
    this.dataSource.data[idx].type_image_url = this.productTaskTypeImagePipe.transform(this.dataSource.data[idx].type);
    const td = this.selectorTypes.toArray()[idx]._elementRef.nativeElement.closest('td');

    setTimeout(() => {
      td?.focus();
    });
  }

  private onDescriptionUpdated(idx: number) {
    this.dataSource.data[idx].description_markdown = this.descriptionToMarkdown(this.dataSource.data[idx].description);
  }

  private evaluateEpicsFirstRows() {
    let last_epic_id: number | undefined | null;
    let epicColorIndex = 0;
    const epicRowColors: { [epicKey: string]: string } = {};
    let epicVisibleRowIndex = 0;
    let epicGroupIndex = -1;

    this.dataSource.data = this.dataSource.data.map(task => {
      task.is_first_epic = last_epic_id === undefined || last_epic_id !== task.company_product_epic_id ? true : false;
      task.epic_row_is_even = false;
      const epicKey = (task.company_product_epic_id ?? 'no_epic').toString();
      if (!(epicKey in epicRowColors)) {
        epicRowColors[epicKey] = this.epicRowColorPalette[epicColorIndex % this.epicRowColorPalette.length];
        epicColorIndex++;
      }
      if (task.is_first_epic) {
        task.epic_row_is_even = epicVisibleRowIndex % 2 === 0;
        epicVisibleRowIndex++;
        epicGroupIndex++;
      }
      task.epic_group_key = this.getEpicGroupKey(task.company_product_epic_id, epicGroupIndex);
      last_epic_id = task.company_product_epic_id;
      return task;
    });
    this.epic_row_colors = epicRowColors;
  }

  private evaluateCompanyProductPhaseMinAndMaxOrders() {
    let min_order: number | undefined, max_order: number | undefined;
    for (const task of this.dataSource.data) {
      min_order = min_order === undefined || min_order > task.order_company_product_phase ? task.order_company_product_phase : min_order;
      max_order = max_order === undefined || max_order < task.order_company_product_phase ? task.order_company_product_phase : max_order;
    }
    if (
      this.filterForm.value.min_order == null ||
      this.filterForm.value.max_order == null ||
      this.filterForm.value.min_order == '' ||
      this.filterForm.value.max_order == ''
    ) {
      this.filterForm.patchValue(
        {
          min_order: min_order,
          max_order: max_order,
        },
        { emitEvent: false }
      );
      this.formPersistence.min_order = min_order;
      this.formPersistence.max_order = max_order;
    }
  }

  private openDescriptionsTinymceAndFocus(idx: number) {
    const currently_editing_idx = this.dataSource.data.findIndex(task => task.editing_description);

    if (currently_editing_idx === idx) {
      this.pending_description_editor_index = null;
      return;
    }
    if (currently_editing_idx !== -1) {
      this.persistAndCloseDescriptionEditor(currently_editing_idx);
    }

    this.pending_description_editor_index = idx;
    this.active_tinymce_init = this.createTinymceInit();
    this.mountPendingDescriptionEditor();
  }

  private mountPendingDescriptionEditor() {
    const idx = this.pending_description_editor_index;
    if (idx === null || !this.dataSource.data[idx]) {
      return;
    }

    this.pending_description_editor_index = null;
    this.dataSource.data[idx].editing_description = true;
    this.cdr.markForCheck();
  }

  private closeDescriptionsTinymceAndFocus(idx: number): void {
    this.pending_description_editor_index = null;
    this.persistAndCloseDescriptionEditor(idx);

    const td = document.querySelector(
      `app-company-product-phase-backlog .mat-mdc-table tr.mat-mdc-row.row-number-${idx + 1} td.mat-column-description`
    ) as HTMLElement | undefined;
    td?.focus();
  }

  private persistAndCloseDescriptionEditor(idx: number): void {
    const task = this.dataSource.data[idx];
    if (!task?.editing_description) {
      return;
    }

    const content = this.tinymceEditor?.editor?.getContent() ?? '';

    if (content !== task.description) {
      this.onProductTaskCustomAttributeUpdate(idx, content, 'description');
    }

    task.editing_description = false;
    this.cdr.markForCheck();
  }

  private createTinymceInit() {
    return {
      automatic_uploads: true,
      statusbar: false,
      menubar: false,
      block_unsupported_drop: false,
      ui_mode: 'split' as const,
    };
  }

  private descriptionToMarkdown(description: string): string {
    const turndownService = this.turndownServiceForDescription();
    return description != null
      ? turndownService.turndown(description.replace(/(?:<br\s*\/?>)?- (.*?)(?=<br\s*\/?>|<\/span>|<\/p>)/g, '<li>$1</li>'))
      : '';
  }

  private deleteRowAtIdx(idx: number) {
    const data = [...this.dataSource.data].map((task, i) => {
      // OJO!! A l'eliminar una task, el backend retrocedeix els order_company_product_phase perquè han de ser
      // consecutius les no eliminades! Ho simulem aquí.
      if (i > idx) {
        task.order_company_product_phase--;
      }
      return task;
    });

    // Eliminem
    data.splice(idx, 1);
    this.dataSource.data = data;

    // Re-evaluem
    this.evaluateEpicsFirstRows();
    this.evaluateCompanyProductPhaseMinAndMaxOrders();
    this.cdr.markForCheck();
  }

  private async createTaskFromIdx(idx: number) {
    const task = this.dataSource.data[idx];

    const forceDraft = this.user_permissions().can_create_tasks_on_backlog && !this.user_permissions().can_mark_tasks_as_ready;

    const task_created = await this.askForTaskOriginCategory(task, forceDraft);

    if (task_created) {
      task.id = task_created.id;
      task.code = task_created.code;
      task.title = task_created.title;
      task.type = task_created.type;
      task.type_image_url = this.productTaskTypeImagePipe.transform(task_created.type);
      task.is_draft = task_created.is_draft;
      task.is_hotfix = task_created.is_hotfix;
      task.needs_testing = task_created.needs_testing;
      task.order_company_product_phase = task_created.order_company_product_phase;
      task.creator_id = this.me!.id;
      task.product_task_origin_category_id = task_created.product_task_origin_category_id;
      this.setTaskCanEdit(task);
      task.budgetables = this.initBudgetablesForTask(task);
      this.syncProductTaskTimeBudgetsFromBudgetables(task);
      this.evaluateEpicsFirstRows();
      this.evaluateCompanyProductPhaseMinAndMaxOrders();
      this.cdr.markForCheck();
    }
  }

  private setTaskCanEdit(task: ProductTaskCustom): void {
    task.can_edit = this.user_permissions().can_edit_backlog || (this.user_permissions().can_create_tasks_on_backlog && task.is_draft);
    task.can_archive_or_delete =
      this.user_permissions().can_archive_or_delete_tasks ||
      (this.user_permissions().can_create_tasks_on_backlog && task.is_draft && task.creator_id === this.me?.id);
  }

  private companyProductPhaseBacklogDataTaskToCustom(item: CompanyProductPhaseBacklogDataProductTask): ProductTaskCustom {
    let task: ProductTask = item.product_task;

    (task as ProductTaskCustom).future_sprint_reviews = item.future_sprint_reviews;
    (task as ProductTaskCustom).selected = false;
    (task as ProductTaskCustom).completed = item.completed;
    (task as ProductTaskCustom).has_time_registered = item.has_time_registered;
    (task as ProductTaskCustom).has_dead_time = true;
    (task as ProductTaskCustom).editing = false;
    (task as ProductTaskCustom).editing_description = false;
    this.setTaskCanEdit(task as ProductTaskCustom);
    (task as ProductTaskCustom).company_product_phase_boards = Object.fromEntries(
      Object.entries(item.company_product_phase_boards).map(([key, ppb]) => {
        const custom_company_product_phase_board = this.companyProductPhaseBoardToCustom(
          ppb.company_product_phase_board,
          Object.values(ppb.distributables)
        );
        return [
          key,
          {
            product_task_board_id: ppb.product_task_board_id,
            company_product_phase_board: custom_company_product_phase_board,
            distributables: this.distributablesToDistributableCustom(
              ppb.distributables,
              custom_company_product_phase_board,
              item.distributables,
              task as ProductTaskCustom
            ),
          },
        ];
      })
    );
    (task as ProductTaskCustom).budgetables = this.initBudgetablesForTask(task, item);
    this.syncProductTaskTimeBudgetsFromBudgetables(task as ProductTaskCustom);
    (task as ProductTaskCustom).description_markdown = this.descriptionToMarkdown(task.description);
    (task as ProductTaskCustom).type_image_url = this.productTaskTypeImagePipe.transform(task.type);
    (task as ProductTaskCustom).total_time_in_seconds = Object.keys((task as ProductTaskCustom).budgetables).reduce(
      (sum, budgetable_key) => {
        return sum + (task as ProductTaskCustom).budgetables[budgetable_key].total_time_budget_in_seconds;
      },
      0
    );
    (task as ProductTaskCustom).total_time_in_hours =
      (task as ProductTaskCustom).total_time_in_seconds > 0
        ? this.roundWithTwoDecimals((task as ProductTaskCustom).total_time_in_seconds / 3600)
        : 0;

    task = this.calcDisabledTimeAutocalcIfAvailable(task as ProductTaskCustom);
    this.refreshTaskCanBeAddedToCurrentBoard(task as ProductTaskCustom);

    return task as ProductTaskCustom;
  }

  private calcDisabledTimeAutocalcIfAvailable(task: ProductTaskCustom): ProductTaskCustom {
    const getKey = (slug: string): string | null => {
      const setting = this.company_product_phase_task_time_autocalc_settings.find(
        set => set.company_product_phase_task_time_autocalc_setting_category.working_time_category?.slug === slug
      );
      return setting
        ? 'App\\Models\\WorkingTimeCategory_' + setting.company_product_phase_task_time_autocalc_setting_category.working_time_category!.id
        : null;
    };

    const taskTimeKey = getKey('task-time-tracking');
    const uxuiKey = getKey('product-design');
    const reviewKey = getKey('review-task');

    const distributable_task_time = taskTimeKey ? task.budgetables[taskTimeKey] : null;
    const distributable_uxui = uxuiKey ? task.budgetables[uxuiKey] : null;
    const distributable_review = reviewKey ? task.budgetables[reviewKey] : null;

    const taskTimeDisabled = distributable_task_time?.in_use_for_autocalc === false;
    const othersEnabled =
      distributable_uxui?.in_use_for_autocalc !== false || // true si existe y está activado, o si no existe
      distributable_review?.in_use_for_autocalc !== false;

    task.disabled_time_autocalc = taskTimeDisabled && othersEnabled;

    return task;
  }

  private distributablesToDistributableCustom(
    distributables: { [key: string]: Distributable },
    parent_company_product_phase_board: CompanyProductPhaseBoard | undefined,
    distributables_general: { [key: string]: Distributable },
    product_task: ProductTaskCustom | undefined = undefined
  ): { [key: string]: DistributableCustom } {
    Object.keys(distributables).map(distributable_key => {
      distributables[distributable_key] = this.distributableToDistributableCustom(
        distributables[distributable_key],
        distributable_key,
        distributables,
        parent_company_product_phase_board,
        distributables_general,
        product_task
      );
    });
    return distributables as { [key: string]: DistributableCustom };
  }

  private distributableToDistributableCustom(
    d: Distributable,
    key: string,
    distributables: { [key: string]: Distributable },
    parent_company_product_phase_board: CompanyProductPhaseBoard | undefined,
    distributables_general: { [key: string]: Distributable },
    product_task: ProductTaskCustom | undefined = undefined
  ): DistributableCustom {
    const type_and_id = key.split('_');
    if (type_and_id.length !== 2) throw new Error('Length must be 2');
    const type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam' = type_and_id[0] as
      | 'App\\Models\\WorkingTimeCategory'
      | 'App\\Models\\CompanyProductTeam';
    const id: number = +type_and_id[1];
    const setting = this.company_product_phase_task_time_autocalc_settings.find(
      s => s.company_product_phase_task_time_autocalc_setting_category.working_time_category_id == id
    );
    if (setting) {
      (d as DistributableCustom).color = setting.color;
      (d as DistributableCustom).background_soft_color = setting.background_soft_color;
      (d as DistributableCustom).progress_bar_color = setting.progress_bar_color;
      (d as DistributableCustom).company_product_phase_task_time_autocalc_setting_id = setting?.id;
      (d as DistributableCustom).in_use_for_autocalc =
        (d as DistributableCustom).total_time_budget_in_seconds > 0 ||
        Object.keys(distributables).every(b_key => distributables[b_key].total_time_budget_in_seconds === 0);
    }

    (d as DistributableCustom).type = type;

    // extres
    d.total_time_assigned_in_hours = this.roundWithTwoDecimals(d.total_time_assigned_in_hours);
    d.total_time_budget_in_hours = this.roundWithTwoDecimals(d.total_time_budget_in_hours);
    d.total_time_done_in_hours = this.roundWithTwoDecimals(d.total_time_done_in_hours);

    const canEditTask = product_task?.can_edit ?? false;
    (d as DistributableCustom).readonly =
      !canEditTask || // No té permisos
      !parent_company_product_phase_board || // No té board la columna
      parent_company_product_phase_board.end_date! < this.now || // El board ja està finalitzat
      !(key in distributables_general) || // No hi ha budget general a la tasca per aquesta categoria
      (product_task !== undefined && product_task.completed) ||
      (product_task !== undefined && product_task.is_draft && !product_task.can_edit);

    return d as DistributableCustom;
  }

  private productTaskBoardTimeBudgetToDistributableCustom(ptbtb: ProductTaskBoardTimeBudget): DistributableCustom {
    const s: CompanyProductPhaseTaskTimeAutocalcSettingCustom | undefined = this.company_product_phase_task_time_autocalc_settings.find(
      s =>
        s.company_product_phase_task_time_autocalc_setting_category.working_time_category?.id == ptbtb.budgetable_id &&
        ptbtb.budgetable_type === 'App\\Models\\WorkingTimeCategory'
    );
    return {
      product_task_board_time_budget_id: ptbtb.id,
      distributable: ptbtb.budgetable,
      total_time_budget_in_seconds: 0,
      total_time_assigned_in_seconds: ptbtb.time_in_seconds ?? 0,
      total_time_done_in_seconds: 0,
      total_time_budget_in_hours: 0,
      total_time_assigned_in_hours: this.roundWithTwoDecimals((ptbtb.time_in_seconds ?? 0) / 3600),
      total_time_done_in_hours: 0,
      completed_by_user_id: null,
      in_use_for_autocalc: true,
      company_product_phase_task_time_autocalc_setting_id: s!.id,
      color: s!.company_product_phase_task_time_autocalc_setting_category.working_time_category!.color,
      background_soft_color: this.getSecondaryColor(
        s!.company_product_phase_task_time_autocalc_setting_category.working_time_category!.color
      ),
      progress_bar_color: this.getSecondaryColor(
        s!.company_product_phase_task_time_autocalc_setting_category.working_time_category!.color,
        0.4
      ),
      type: ptbtb.budgetable_type,
    } as DistributableCustom;
  }

  private createDistributableCustomFromSetting(
    setting: CompanyProductPhaseTaskTimeAutocalcSettingCustom,
    total_time_budget_in_seconds: number = 0
  ): DistributableCustom {
    const working_time_category = setting.company_product_phase_task_time_autocalc_setting_category.working_time_category;
    return {
      product_task_time_budget_id: undefined,
      distributable: working_time_category!,
      total_time_budget_in_seconds: total_time_budget_in_seconds,
      total_time_assigned_in_seconds: 0,
      total_time_done_in_seconds: 0,
      total_time_budget_in_hours: total_time_budget_in_seconds > 0 ? this.roundWithTwoDecimals(total_time_budget_in_seconds / 3600) : 0,
      total_time_assigned_in_hours: 0,
      total_time_done_in_hours: 0,
      in_use_for_autocalc: true,
      company_product_phase_task_time_autocalc_setting_id: setting.id,
      completed_by_user_id: null,
      color: setting.color,
      background_soft_color: setting.background_soft_color,
      progress_bar_color: setting.progress_bar_color,
      product_task_board_time_budget_id: undefined,
      readonly: false,
      type: 'App\\Models\\WorkingTimeCategory',
      editing: false,
    };
  }

  private createDistributableCustomFromCompanyProductTeam(
    team: CompanyProductTeamCustom,
    total_time_budget_in_seconds: number = 0
  ): DistributableCustom {
    const distributable: DistributableCustom = {
      product_task_time_budget_id: undefined,
      distributable: team,
      total_time_budget_in_seconds: total_time_budget_in_seconds,
      total_time_assigned_in_seconds: 0,
      total_time_done_in_seconds: 0,
      total_time_budget_in_hours: total_time_budget_in_seconds > 0 ? this.roundWithTwoDecimals(total_time_budget_in_seconds / 3600) : 0,
      total_time_assigned_in_hours: 0,
      total_time_done_in_hours: 0,
      in_use_for_autocalc: true,
      company_product_phase_task_time_autocalc_setting_id: 0,
      completed_by_user_id: null,
      color: team.color,
      background_soft_color: team.background_soft_color,
      progress_bar_color: team.progress_bar_color,
      product_task_board_time_budget_id: undefined,
      readonly: false,
      type: 'App\\Models\\CompanyProductTeam',
      editing: false,
    };

    return distributable;
  }

  private moveItemsInArray(dataSource: MatTableDataSource<ProductTaskCustom>, previousIndexes: number[], currentIndex: number) {
    // 1. Clona el array de datos
    const data = this.dataSource.data.slice();

    // 2. Ordena los índices de mayor a menor
    const sortedIndexes = previousIndexes.slice().sort((a, b) => b - a);

    // 3. Guarda los elementos que vas a mover (en orden original)
    const items_to_move = previousIndexes.map(i => data[i]);

    // 4. Elimina los elementos desde los índices más altos
    for (const i of sortedIndexes) {
      data.splice(i, 1);
    }

    // 5. Ajusta currentIndex restando los elementos eliminados antes de él
    const numRemovedBefore = previousIndexes.filter(i => i < currentIndex).length;
    const isDraggingDown = previousIndexes.every(i => i < currentIndex);
    const adjustedIndex = currentIndex - numRemovedBefore + (isDraggingDown ? 1 : 0);

    // 6. Inserta en la posición correcta
    data.splice(adjustedIndex, 0, ...items_to_move);

    // 7. Asigna el nuevo array
    dataSource.data = data;
  }

  private getCurrentAndPreviousIndexOnDroppedEvent({ previousIndex, currentIndex }: CdkDragDrop<ProductTaskCustom[]>) {
    /**
     * previousIndex i currentIndex son els index tenint en compte les rows de EPICS.
     * No són els index de datasource.data!!!
     * Per lo tant el primer a fer és transformar-los a datasource.data indexes
     */
    const max_dragndrop_idx: number = Math.max(previousIndex, currentIndex);
    const struct_from_dragndrop_idx_to_datasource_idx: number[] = [];
    let datasource_idx: number = 0;
    let dragndrop_idx: number = 0;
    while (dragndrop_idx <= max_dragndrop_idx) {
      const t = this.dataSource.data[datasource_idx];
      struct_from_dragndrop_idx_to_datasource_idx[dragndrop_idx] = datasource_idx;

      if (t.is_first_epic) {
        dragndrop_idx++;
        struct_from_dragndrop_idx_to_datasource_idx[dragndrop_idx] = datasource_idx;
      }

      dragndrop_idx++;
      datasource_idx++;
    }

    const prev_datasource_index: number = struct_from_dragndrop_idx_to_datasource_idx[previousIndex];
    const curr_datasource_index: number = struct_from_dragndrop_idx_to_datasource_idx[currentIndex];

    return [prev_datasource_index, curr_datasource_index];
  }

  private selectColumnsByUrl() {
    if (this.activatedRoute.routeConfig!.path === 'backlog') {
      this.showBacklog();
    } else if (this.activatedRoute.routeConfig!.path === 'roadmap') {
      if (this.company_product_phase && this.company_product_phase.enable_roadmap) {
        this.showRoadmap();
      } else {
        this.router.navigate(['backlog']);
      }
    }
  }

  private addTaskBudgetsColumns() {
    // add on displayed columns all the keys from company_product_phase_task_time_autocalc_settings after time_autocalc
    const keys: string[] = this.company_product_phase_task_time_autocalc_settings
      .filter(
        item =>
          item.company_product_phase_task_time_autocalc_setting_category.working_time_category_id !== null &&
          (this.tab_selected === 'backlog' || item.company_product_phase_task_time_autocalc_setting_category.slug !== 'review-task')
      )
      .map((item: CompanyProductPhaseTaskTimeAutocalcSettingCustom) => item.key)
      .reverse();

    keys.push(...this.company_product_teams.map((item: CompanyProductTeamCustom) => item.key));

    this.task_budgets_column_count = keys.length;

    this.columns = [...this.company_product_phase_task_time_autocalc_settings, ...this.company_product_teams];

    keys.forEach((key, idx) => {
      const s = this.company_product_phase_task_time_autocalc_settings.find(s => s.key === key);
      if (s) {
        // 'calc(34px + 32px + 60px + 400px + ' + (j-(company_product_phase_task_time_autocalc_settings.length-task_budgets_column_count))*60 + 'px)'
        s.left = 32 + 32 + 34 + 32 + 65 + 400 + idx * 60 + 'px';
      }
    });

    this.spliceColumns(this.displayedColumns.indexOf(this.tab_selected === 'backlog' ? 'time_autocalc' : 'title') + 1, 0, ...keys);
  }

  private calculateTimeAutocalc(index: number, value: string) {
    const t = this.dataSource.data[index];
    const slugs = ['product-design', 'review-task', 'task-time-tracking'];

    const budgetables = Object.values(t.budgetables) as DistributableCustom[];

    const params = {
      time_in_seconds: new HourStringToSecondsPipe().transform(value),
      needs_uxui: +(budgetables.find(b => (b.distributable as WorkingTimeCategory).slug === 'product-design')?.in_use_for_autocalc ?? true),
      needs_review: +(budgetables.find(b => (b.distributable as WorkingTimeCategory).slug === 'review-task')?.in_use_for_autocalc ?? true),
      needs_task_time_tracking: +(
        budgetables.find(b => (b.distributable as WorkingTimeCategory).slug === 'task-time-tracking')?.in_use_for_autocalc ?? true
      ),
    };

    this.api.autocalcTaskTime(t.id, params).subscribe((data: { [key: string]: number }) => {
      t.total_time_in_seconds = slugs.reduce((sum, key) => sum + (data[key] || 0), 0);
      t.total_time_in_hours = this.roundWithTwoDecimals(t.total_time_in_seconds / 3600 || 0);
      this.propagateTaskTime(t, data);
    });

    // clear input
    const input = document.getElementsByClassName('time-autocalc-input')[index] as HTMLInputElement;
    if (input) input.value = '';
  }

  private async updateTaskTimeCategory(
    idx: number,
    setting: CompanyProductPhaseTaskTimeAutocalcSettingCustom | CompanyProductTeamCustom,
    seconds: number
  ) {
    const t = this.dataSource.data[idx];
    let key: string;

    if (setting.type === 'CompanyProductPhaseTaskTimeAutocalcSettingCustom') {
      key =
        'App\\Models\\WorkingTimeCategory_' +
        (setting as CompanyProductPhaseTaskTimeAutocalcSettingCustom).company_product_phase_task_time_autocalc_setting_category
          .working_time_category!.id;
    } else {
      key = 'App\\Models\\CompanyProductTeam_' + (setting as CompanyProductTeamCustom).id;
    }

    if (key in t.budgetables) {
      if (seconds === 0) {
        const budgetable = t.budgetables[key];
        if (t.id != null) {
          await this.removeProductTaskTimeBudget(t, key, budgetable);
        }
        delete t.budgetables[key];
        this.syncProductTaskTimeBudgetsFromBudgetables(t);
      } else {
        t.budgetables[key].total_time_budget_in_seconds = seconds;
        t.budgetables[key].total_time_budget_in_hours = this.roundWithTwoDecimals(seconds / 3600);
        t.budgetables[key].in_use_for_autocalc = true;
        t.budgetables[key].editing = false;
      }
    } else if (seconds > 0) {
      if (setting.type === 'CompanyProductPhaseTaskTimeAutocalcSettingCustom')
        t.budgetables[key] = this.createDistributableCustomFromSetting(
          setting as CompanyProductPhaseTaskTimeAutocalcSettingCustom,
          seconds
        );
      else t.budgetables[key] = this.createDistributableCustomFromCompanyProductTeam(setting as CompanyProductTeamCustom, seconds);
    }

    // Calculate total time
    let total = 0;
    Object.keys(t.budgetables).forEach((budgetable_key: string) => {
      if (t.budgetables[budgetable_key].in_use_for_autocalc) {
        total += t.budgetables[budgetable_key].total_time_budget_in_seconds;
      }
    });
    t.total_time_in_seconds = total;
    t.total_time_in_hours = this.roundWithTwoDecimals(total > 0 ? total / 3600 : 0);

    await this.updateProductTaskTimeBudgets(t, t.budgetables);
    this.cdr.markForCheck();
  }

  private propagateTaskTime(task: ProductTaskCustom, data: { [key: string]: number }) {
    const budgetables: { [key: string]: DistributableCustom } = task.budgetables;
    const settings = this.company_product_phase_task_time_autocalc_settings.filter(s =>
      ['product-design', 'review-task', 'task-time-tracking'].includes(s.company_product_phase_task_time_autocalc_setting_category.slug)
    );

    settings.forEach((set: CompanyProductPhaseTaskTimeAutocalcSettingCustom) => {
      const slug = set.company_product_phase_task_time_autocalc_setting_category.slug;
      const budgetable_key =
        'App\\Models\\WorkingTimeCategory_' + set.company_product_phase_task_time_autocalc_setting_category.working_time_category?.id;

      if (budgetable_key in budgetables && budgetables[budgetable_key].in_use_for_autocalc) {
        this.updateLocalTimeBudget(budgetables[budgetable_key], data[slug]);
      }
    });
    void this.updateProductTaskTimeBudgets(task, budgetables);
  }

  private refreshTaskCanBeAddedToCurrentBoard(task: ProductTaskCustom): void {
    task.can_be_added_to_current_board =
      this.tab_selected === 'backlog' &&
      this.user_permissions().can_edit_board &&
      !task.completed &&
      !task.is_draft &&
      this.currentBoardId !== null &&
      !task.company_product_phase_boards['company_product_phase_board_' + this.currentBoardId];
  }

  private refreshAllTasksCanBeAddedToCurrentBoard(): void {
    this.dataSource.data.forEach((task: ProductTaskCustom): void => {
      this.refreshTaskCanBeAddedToCurrentBoard(task);
    });
  }

  private parseBudgetableKey(budgetable_key: string): {
    budgetable_type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam';
    budgetable_id: number;
  } {
    const separator_index = budgetable_key.lastIndexOf('_');
    if (separator_index === -1) {
      throw new Error('Invalid budgetable key');
    }

    return {
      budgetable_type: budgetable_key.slice(0, separator_index) as 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam',
      budgetable_id: Number(budgetable_key.slice(separator_index + 1)),
    };
  }

  private resolveProductTaskTimeBudgetId(
    task: ProductTaskCustom,
    budgetable_key: string,
    budgetable?: DistributableCustom
  ): number | undefined {
    if (budgetable?.product_task_time_budget_id) {
      return budgetable.product_task_time_budget_id;
    }

    const { budgetable_type, budgetable_id } = this.parseBudgetableKey(budgetable_key);

    return task.product_task_time_budgets?.find(pttb => pttb.budgetable_type === budgetable_type && pttb.budgetable_id === budgetable_id)
      ?.id;
  }

  private removeProductTaskTimeBudgetFromLocalState(
    task: ProductTaskCustom,
    budgetable_type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam',
    budgetable_id: number,
    budget_id?: number
  ): void {
    if (budget_id && task.product_task_time_budgets) {
      task.product_task_time_budgets = task.product_task_time_budgets.filter(pttb => pttb.id !== budget_id);
    } else if (task.product_task_time_budgets) {
      task.product_task_time_budgets = task.product_task_time_budgets.filter(
        pttb => !(pttb.budgetable_type === budgetable_type && pttb.budgetable_id === budgetable_id)
      );
    }
  }

  private async removeProductTaskTimeBudget(
    task: ProductTaskCustom,
    budgetable_key: string,
    budgetable?: DistributableCustom
  ): Promise<void> {
    const { budgetable_type, budgetable_id } = this.parseBudgetableKey(budgetable_key);
    const budget_id = this.resolveProductTaskTimeBudgetId(task, budgetable_key, budgetable);

    try {
      if (budget_id != null) {
        await firstValueFrom(this.api.deleteProductTaskTimeBudget(budget_id));
      } else {
        await firstValueFrom(
          this.api.updateProductTaskTimeBudgets(
            task.id,
            [
              {
                product_task_id: task.id,
                budgetable_type,
                budgetable_id,
                time_in_seconds: 0,
              } as ProductTaskTimeBudget,
            ],
            true
          )
        );
      }

      this.removeProductTaskTimeBudgetFromLocalState(task, budgetable_type, budgetable_id, budget_id);
    } catch {
      this.snack.open('No se pudo quitar el presupuesto de la tarea.', 'Ok', { duration: 5000 });
      throw new Error('Could not remove company_product time budget');
    }
  }

  private getTaskLevelDistributablesFromBacklogItem(distributables: { [key: string]: Distributable }): {
    [key: string]: Distributable;
  } {
    return Object.fromEntries(
      Object.entries(distributables).filter(([, distributable]) => distributable.product_task_time_budget_id != null)
    );
  }

  private syncProductTaskTimeBudgetsFromBudgetables(task: ProductTaskCustom): void {
    task.product_task_time_budgets = Object.entries(task.budgetables)
      .filter(([, distributable]) => distributable.product_task_time_budget_id != null)
      .map(([budgetable_key, distributable]) => {
        const { budgetable_type, budgetable_id } = this.parseBudgetableKey(budgetable_key);

        return {
          id: distributable.product_task_time_budget_id!,
          product_task_id: task.id,
          budgetable_type: distributable.type ?? budgetable_type,
          budgetable_id: distributable.distributable.id ?? budgetable_id,
          time_in_seconds: distributable.total_time_budget_in_seconds,
        } as ProductTaskTimeBudget;
      });
  }

  private async updateProductTaskTimeBudgets(task: ProductTaskCustom, budgetables: { [key: string]: DistributableCustom }): Promise<void> {
    if (task.id == null) {
      return;
    }

    const res: ProductTaskTimeBudget[] = [];

    for (const [budgetable_key, budgetable] of Object.entries(budgetables)) {
      if (!budgetable.in_use_for_autocalc || budgetable.total_time_budget_in_seconds <= 0) {
        continue;
      }

      const { budgetable_type, budgetable_id } = this.parseBudgetableKey(budgetable_key);
      const existing_budget = task.product_task_time_budgets?.find(
        pttb => pttb.budgetable_type === budgetable_type && pttb.budgetable_id === budgetable_id
      );

      res.push({
        id: existing_budget?.id ?? budgetable.product_task_time_budget_id,
        product_task_id: task.id,
        budgetable_type: budgetable.type ?? budgetable_type,
        budgetable_id: budgetable.distributable.id ?? budgetable_id,
        time_in_seconds: budgetable.total_time_budget_in_seconds,
      } as ProductTaskTimeBudget);
    }

    if (res.length === 0) {
      return;
    }

    await firstValueFrom(this.api.updateProductTaskTimeBudgets(task.id, res));
    this.syncProductTaskTimeBudgetsFromBudgetables(task);
  }

  private updateLocalTimeBudget(budgetable: DistributableCustom, seconds: number) {
    const roundedSeconds = Math.round(seconds * 100) / 100;
    const roundedHours = seconds > 0 ? Math.round((seconds / 3600) * 100) / 100 : 0;

    budgetable.total_time_budget_in_seconds = roundedSeconds;
    budgetable.total_time_budget_in_hours = roundedHours;
  }

  private spliceColumns(idx: number, deleteCount: number, ...items: string[]) {
    this.displayedColumns.splice(idx, deleteCount, ...items);
    this.displayedColumns_extra_head1.splice(idx, deleteCount, ...items.map(key => key + '_head1'));
    this.displayedColumns_extra_head2.splice(idx, deleteCount, ...items.map(key => key + '_head2'));
    this.displayedColumns_extra_head3.splice(idx, deleteCount, ...items.map(key => key + '_head3'));
    this.recalculateEpicsOnlyColspans();
    this.cdr.markForCheck();
  }

  private getSecondaryColor(color: string, lightenFactor: number = 0.7) {
    // apply transparency
    const hexColor = color.replace('#', '');
    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    // Aclarar cada componente hacia 255 (blanco)
    const lighten = (c: number) => Math.round(c + (255 - c) * lightenFactor);

    const rLight = lighten(r);
    const gLight = lighten(g);
    const bLight = lighten(b);

    return `rgb(${rLight}, ${gLight}, ${bLight})`;
  }

  private async onUpdateProductTaskBoardTimeBudget(
    task_idx: number,
    company_product_phase_board_key: string,
    budgetable_key: string,
    time_in_hours: number
  ) {
    const budgets_to_update: { budgetable_type: string; budgetable_id: number; time_in_seconds: number }[] = [
      {
        budgetable_type: budgetable_key.split('_')[0],
        budgetable_id: +budgetable_key.split('_')[1],
        time_in_seconds: Math.round(time_in_hours * 3600),
      },
    ];
    const product_task_board_exists: boolean =
      company_product_phase_board_key in this.dataSource.data[task_idx].company_product_phase_boards;
    const company_product_phase_board_id: number = +company_product_phase_board_key.split('company_product_phase_board_')[1];
    const roadmap_column_key: string = Object.keys(this.roadmap_columns_data).find(
      k =>
        this.roadmap_columns_data[k].company_product_phase_board &&
        this.roadmap_columns_data[k].company_product_phase_board.id === company_product_phase_board_id
    )!;

    // 1. Calcular REV proporcionalment
    const rev: { budgetable_type: string; budgetable_id: number; time_in_seconds: number } | undefined =
      this.autocalcRevAfterBoardTimeBudgetSet(task_idx, company_product_phase_board_key, budgetable_key, time_in_hours * 3600);
    if (!rev) {
      this.snack.open('asdf');
      throw new Error('No REV Autocalc Setting Category');
    }
    budgets_to_update.push(rev);

    let updated: boolean = false;

    // 2. EXECUTAR CANVIS
    if (!product_task_board_exists && time_in_hours > 0) {
      // Crear a bbdd amb budgets
      await this.createProductTaskBoardFromBudgetCreation(
        task_idx,
        company_product_phase_board_key,
        roadmap_column_key,
        budgetable_key,
        budgets_to_update
      );
      updated = true;
    } else if (product_task_board_exists) {
      budgets_to_update.forEach(async bud => {
        const b_key: string = bud.budgetable_type + '_' + bud.budgetable_id;
        const budget_exists_on_board: boolean =
          b_key in this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables;

        // COLUMN
        if (budget_exists_on_board) {
          const has_value_changed: boolean =
            this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[b_key]
              .total_time_assigned_in_seconds !== bud.time_in_seconds;
          if (has_value_changed) {
            await this.updateExistingProductTaskBoardTimeBudgetLocallyAndRemotely(
              task_idx,
              company_product_phase_board_key,
              roadmap_column_key,
              b_key,
              bud
            );
            updated = true;
          }
        } else if (time_in_hours > 0) {
          // crear en bbdd
          this.createProductTaskBoardTimeBudgetFromRoadmapUserInput(
            task_idx,
            company_product_phase_board_key,
            roadmap_column_key,
            b_key,
            bud
          );
          updated = true;
        }
      });
    }

    if (updated) {
      this.cdr.markForCheck();
    }
  }

  private autocalcRevAfterBoardTimeBudgetSet(
    task_idx: number,
    company_product_phase_board_key: string,
    budgetable_key: string,
    time_in_seconds: number
  ): { budgetable_type: string; budgetable_id: number; time_in_seconds: number } | undefined {
    /**
     * Exemple:
     * ---------
     * - DEV = 10h (32% en setting) <-- és el que estem intentant setejar
     * - UXUI = 3h (15% en setting)
     * - REV = ??? (3% en setting) <-- "x"
     *
     * Llavors vol dir que si consideréssim només categories "considerables" seria:
     * - DEV = 32/(32+15+3) = 64%
     * - UXUI = 15/(32+15+3) = 30%
     * - REV = 3/(32+15+3) = 6%
     *
     * Llavors vol dir que, fent una regla de 3:
     *    10+3  ---->  64+30
     *    x     ---->  6
     *
     * x = 6*(10+3)/(64+30) = 0,82h
     */
    const b_type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam' = budgetable_key.split('_')[0] as
      | 'App\\Models\\WorkingTimeCategory'
      | 'App\\Models\\CompanyProductTeam';
    const b_id: number = +budgetable_key.split('_')[1];
    let rev_budgetable_id: number | undefined;
    let rev_budgetable_type: string | undefined;
    const categories_with_rev: { slug: string; set_prcnt: number; time_in_seconds: number; overall_prcnt: number }[] = [];
    let categories_without_rev: { slug: string; set_prcnt: number; time_in_seconds: number; overall_prcnt: number }[] = [];

    // 1. Trobar "set_prcnt" i "time_in_seconds"
    if (company_product_phase_board_key in this.dataSource.data[task_idx].company_product_phase_boards) {
      Object.keys(this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables).forEach(
        b_key => {
          const [budgetable_type, budgetable_id] = b_key.split('_');
          const s = this.company_product_phase_task_time_autocalc_settings.find(
            s =>
              s.id ===
              this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[b_key]
                .company_product_phase_task_time_autocalc_setting_id
          );
          if (s) {
            if (s.company_product_phase_task_time_autocalc_setting_category.working_time_category) {
              const item = {
                slug: s.company_product_phase_task_time_autocalc_setting_category.working_time_category.slug,
                set_prcnt: s.prcnt,
                time_in_seconds:
                  this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[b_key]
                    .total_time_assigned_in_seconds,
                overall_prcnt: undefined as any,
              };

              if (s.company_product_phase_task_time_autocalc_setting_category.working_time_category.slug === 'review-task') {
                rev_budgetable_id = s.company_product_phase_task_time_autocalc_setting_category.working_time_category.id;
                rev_budgetable_type = 'App\\Models\\WorkingTimeCategory';
              }

              categories_with_rev.push(item);
            }
          }
        }
      );
    } else {
      const settings = this.company_product_phase_task_time_autocalc_settings.filter(
        s =>
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category?.slug === 'review-task' ||
          s.company_product_phase_task_time_autocalc_setting_category.working_time_category?.id === b_id
      );
      settings.forEach(s => {
        if (s.company_product_phase_task_time_autocalc_setting_category.working_time_category) {
          const item = {
            slug: s.company_product_phase_task_time_autocalc_setting_category.working_time_category.slug,
            set_prcnt: s.prcnt,
            time_in_seconds:
              s.company_product_phase_task_time_autocalc_setting_category.working_time_category.id === b_id &&
              b_type === 'App\\Models\\WorkingTimeCategory'
                ? time_in_seconds
                : 0,
            overall_prcnt: undefined as any,
          };

          if (s.company_product_phase_task_time_autocalc_setting_category.working_time_category.slug === 'review-task') {
            rev_budgetable_id = s.company_product_phase_task_time_autocalc_setting_category.working_time_category.id;
            rev_budgetable_type = 'App\\Models\\WorkingTimeCategory';
          }

          categories_with_rev.push(item);
        }
      });
    }

    // 1.1. Check extra si no existe REV autocalc category
    if (!rev_budgetable_type || !rev_budgetable_id) {
      const rev_set = this.company_product_phase_task_time_autocalc_settings.find(
        s => s.company_product_phase_task_time_autocalc_setting_category.slug === 'review-task'
      );
      if (!rev_set)
        return undefined; // no s'ha trobat REV autocalc category
      else {
        rev_budgetable_id = rev_set.company_product_phase_task_time_autocalc_setting_category.working_time_category!.id;
        rev_budgetable_type = 'App\\Models\\WorkingTimeCategory';
        categories_with_rev.push({
          slug: rev_set.company_product_phase_task_time_autocalc_setting_category.working_time_category!.slug,
          set_prcnt: rev_set.prcnt,
          time_in_seconds: undefined as any,
          overall_prcnt: undefined as any,
        });
      }
    }

    // 2. Trobar "overall_prcnt"
    categories_with_rev.forEach(c => {
      c.overall_prcnt = (c.set_prcnt / categories_with_rev.map(c => c.set_prcnt).reduce((carry, set_prcnt) => carry + set_prcnt, 0)) * 100;
    });

    // 3. Fer categories_without_rev
    categories_without_rev = categories_with_rev.filter(c => c.slug !== 'review-task');

    // 4. Fem regla de tres
    const rev_cat = categories_with_rev.find(c => c.slug === 'review-task');
    if (!rev_cat) return undefined;
    const rev_overall_prcnt = rev_cat.overall_prcnt;
    const rev_value_in_seconds = Math.round(
      (rev_overall_prcnt * categories_without_rev.reduce((carry, curr) => carry + curr.time_in_seconds, 0)) /
        categories_without_rev.reduce((carry, curr) => carry + curr.overall_prcnt, 0)
    );

    return { budgetable_id: rev_budgetable_id, budgetable_type: rev_budgetable_type, time_in_seconds: rev_value_in_seconds };
  }

  private roundWithTwoDecimals(num: number): number {
    return Math.round(num * 100) / 100;
  }

  private async createProductTaskBoardFromBudgetCreation(
    task_idx: number,
    company_product_phase_board_key: string,
    roadmap_column_key: string,
    budgetable_key: string,
    budgets_to_update: { budgetable_type: string; budgetable_id: number; time_in_seconds: number }[]
  ) {
    const company_product_phase_board_id: number = +company_product_phase_board_key.split('company_product_phase_board_')[1];
    const response = await this.api
      .createProductTaskBoard(
        {
          product_task_id: this.dataSource.data[task_idx].id,
          company_product_phase_board_id: company_product_phase_board_id,
          product_task_board_time_budgets: budgets_to_update as ProductTaskBoardTimeBudget[],
        } as any,
        true
      )
      .toPromise();

    if (response) {
      // Crear en local

      // Actualitzem CELL
      this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key] = {
        product_task_board_id: response.id,
        company_product_phase_board: this.roadmap_columns_data[roadmap_column_key].company_product_phase_board!,
        distributables: response.product_task_board_time_budgets.reduce(
          (obj: { [key: string]: DistributableCustom }, ptbtb: ProductTaskBoardTimeBudget) => {
            const key: string = ptbtb.budgetable_type + '_' + ptbtb.budgetable_id;
            obj[key] = this.productTaskBoardTimeBudgetToDistributableCustom(ptbtb);
            return obj;
          },
          {} as { [key: string]: DistributableCustom }
        ),
      };

      // Actualitzem COLUMN i ROW
      budgets_to_update.forEach(bud => {
        // COLUMN
        const ptbtb: DistributableCustom | undefined = Object.values(
          this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets
        ).find(
          ptbtb =>
            (ptbtb.distributable as WorkingTimeCategory).id === bud.budgetable_id &&
            bud.budgetable_type === 'App\\Models\\WorkingTimeCategory'
        );
        if (ptbtb) {
          ptbtb.total_time_assigned_in_seconds += bud.time_in_seconds;
          ptbtb.total_time_assigned_in_hours += bud.time_in_seconds / 3600;

          // Actualtizem ROW
          if (!(budgetable_key in this.dataSource.data[task_idx].budgetables)) {
            this.dataSource.data[task_idx].budgetables[budgetable_key] = this.distributableToDistributableCustom(
              { ...ptbtb },
              budgetable_key,
              this.dataSource.data[task_idx].budgetables,
              undefined,
              this.dataSource.data[task_idx].budgetables
            );
          } else {
            // ROW
            this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_hours += bud.time_in_seconds / 3600;
            this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_seconds += bud.time_in_seconds;
          }
        }
      });

      // Actualitzem BOARD attributes
      this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
    }
  }

  private async updateExistingProductTaskBoardTimeBudgetLocallyAndRemotely(
    task_idx: number,
    company_product_phase_board_key: string,
    roadmap_column_key: string,
    budgetable_key: string,
    bud: { budgetable_type: string; budgetable_id: number; time_in_seconds: number }
  ) {
    const diff_in_seconds: number =
      bud.time_in_seconds -
      this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[budgetable_key]
        .total_time_assigned_in_seconds;
    const diff_in_hours: number = diff_in_seconds / 3600;

    // actualitzar en local
    this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[
      budgetable_key
    ].total_time_assigned_in_hours = bud.time_in_seconds / 3600;
    this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[
      budgetable_key
    ].total_time_assigned_in_seconds = bud.time_in_seconds;
    // actualitzar a bbdd
    this.api
      .updateProductTaskBoardTimeBudget(
        this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[budgetable_key]
          .product_task_board_time_budget_id!,
        {
          time_in_seconds: bud.time_in_seconds,
        },
        true
      )
      .subscribe();

    // Actualitzem COLUMN
    const ptbtb: DistributableCustom | undefined = Object.values(
      this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets
    ).find(
      ptbtb =>
        (ptbtb.distributable as WorkingTimeCategory).id === bud.budgetable_id && bud.budgetable_type === 'App\\Models\\WorkingTimeCategory'
    );
    if (ptbtb) {
      ptbtb.total_time_assigned_in_seconds += diff_in_seconds;
      ptbtb.total_time_assigned_in_hours += diff_in_hours;

      // Actualitzem BOARD attributes
      this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);
    }

    // Actualtizem ROW
    this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_hours += diff_in_hours;
    this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_seconds += diff_in_seconds;
  }

  private async createProductTaskBoardTimeBudgetFromRoadmapUserInput(
    task_idx: number,
    company_product_phase_board_key: string,
    roadmap_column_key: string,
    budgetable_key: string,
    bud: { budgetable_type: string; budgetable_id: number; time_in_seconds: number }
  ) {
    const ptbtb = await this.api
      .createProductTaskBoardTimeBudget(
        {
          product_task_board_id:
            this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].product_task_board_id,
          budgetable_type: bud.budgetable_type,
          budgetable_id: bud.budgetable_id,
          time_in_seconds: bud.time_in_seconds,
        } as ProductTaskBoardTimeBudget,
        true
      )
      .toPromise();

    if (ptbtb) {
      // crear en local
      this.dataSource.data[task_idx].company_product_phase_boards[company_product_phase_board_key].distributables[budgetable_key] =
        this.productTaskBoardTimeBudgetToDistributableCustom(ptbtb);

      // Actualitzem COLUMN
      const ptbtb_col: DistributableCustom | undefined = Object.values(
        this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets
      ).find(
        ptbtb =>
          (ptbtb.distributable as WorkingTimeCategory).id === bud.budgetable_id &&
          bud.budgetable_type === 'App\\Models\\WorkingTimeCategory'
      );
      if (ptbtb_col) {
        ptbtb_col.total_time_assigned_in_seconds += bud.time_in_seconds;
        ptbtb_col.total_time_assigned_in_hours += bud.time_in_seconds / 3600;

        // Actualitzem BOARD attributes
        this.refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key);

        // Actualtizem ROW
        if (!(budgetable_key in this.dataSource.data[task_idx].budgetables)) {
          this.dataSource.data[task_idx].budgetables[budgetable_key] = this.distributableToDistributableCustom(
            { ...ptbtb_col },
            budgetable_key,
            this.dataSource.data[task_idx].budgetables,
            undefined,
            this.dataSource.data[task_idx].budgetables
          );
        } else {
          this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_hours += bud.time_in_seconds / 3600;
          this.dataSource.data[task_idx].budgetables[budgetable_key].total_time_assigned_in_seconds += bud.time_in_seconds;
        }
      }
    }
  }

  private companyProductPhaseBoardToCustom(
    company_product_phase_board: CompanyProductPhaseBoard,
    distributables: Distributable[] = []
  ): CompanyProductPhaseBoardCustom {
    const res = { ...company_product_phase_board } as CompanyProductPhaseBoardCustom;
    if (res.start_date && typeof res.start_date === 'string') res.start_date = new Date(res.start_date);
    if (res.end_date && typeof res.end_date === 'string') res.end_date = new Date(res.end_date);
    res.is_deletable = distributables.reduce(
      (carry: boolean, curr: Distributable) =>
        carry || (curr.total_time_assigned_in_seconds === 0 && curr.total_time_done_in_seconds === 0),
      false
    );
    return res;
  }

  private refreshCompanyProductPhaseBoardRoadmapColumn(roadmap_column_key: string) {
    if (roadmap_column_key in this.roadmap_columns_data && this.roadmap_columns_data[roadmap_column_key].company_product_phase_board) {
      this.roadmap_columns_data[roadmap_column_key].company_product_phase_board = this.companyProductPhaseBoardToCustom(
        this.roadmap_columns_data[roadmap_column_key].company_product_phase_board,
        Object.values(this.roadmap_columns_data[roadmap_column_key].product_task_board_time_budgets)
      );
    }
  }

  private async fetchCompanyProduct() {
    this.company_product = await this.api.getCompanyProduct(this.company_product_id.toString()).toPromise();
  }

  private async fetchCompanyProductPhase() {
    this.company_product_phase = await this.api.getCompanyProductPhase(this.company_product_phase_id).toPromise();
  }

  private async fetchActiveCompanyProductPhaseBoard(): Promise<void> {
    const boards = await firstValueFrom(this.api.getPhaseBoards(this.company_product_phase_id, { scopes: 'active' }));
    this.active_company_product_phase_board = boards.length
      ? this.companyProductPhaseBoardToCustom(this.pickCurrentActiveBoard(boards)!)
      : null;
    this.refreshAllTasksCanBeAddedToCurrentBoard();

    this.cdr.markForCheck();
  }

  private pickCurrentActiveBoard(boards: CompanyProductPhaseBoard[]): CompanyProductPhaseBoard | null {
    if (!boards.length) return null;

    const now = this.now.getTime();
    let bounded_active: CompanyProductPhaseBoard | null = null;
    let bounded_active_start = 0;

    for (const board of boards) {
      if (!board.start_date || !board.end_date) continue;

      const start = new Date(board.start_date).getTime();
      const end = new Date(board.end_date).getTime();

      if (start <= now && now <= end && start > bounded_active_start) {
        bounded_active = board;
        bounded_active_start = start;
      }
    }

    if (bounded_active) return bounded_active;

    return boards.reduce((latest: CompanyProductPhaseBoard, board: CompanyProductPhaseBoard): CompanyProductPhaseBoard => {
      const latest_start = latest.start_date ? new Date(latest.start_date).getTime() : 0;
      const board_start = board.start_date ? new Date(board.start_date).getTime() : 0;
      return board_start > latest_start ? board : latest;
    });
  }

  private getActiveBoardIdFromColumns(columns: RoadmapColumnData[]): number | null {
    const now = this.now.getTime();
    let active: { id: number; start_date: Date } | null = null;

    for (const col of columns) {
      const board = col.company_product_phase_board;
      if (board?.id == null) continue;

      const id = Number(board.id);
      if (Number.isNaN(id)) continue;

      const start = col.start_date instanceof Date ? col.start_date.getTime() : new Date(col.start_date).getTime();
      const end = col.end_date instanceof Date ? col.end_date.getTime() : new Date(col.end_date).getTime();

      if (start <= now && now <= end && (active === null || start > active.start_date.getTime())) {
        active = { id, start_date: col.start_date };
      }
    }

    return active?.id ?? null;
  }

  private calcTaskIdxsToProcessAfterDropping(
    previousIndex: number,
    currentIndex: number,
    event: CdkDragDrop<ProductTaskCustom[]>
  ): number[] {
    const dragData = event.item.data as CdkDragDataCustom;
    let task_idxs_to_process: number[] = [];

    if (this.selected_task_idxs.length > 0) {
      task_idxs_to_process = [...this.selected_task_idxs];
    } else if (dragData.type === 'epic') {
      let i: number = dragData.idx;
      const epic_to_check_id: number = dragData.epic!.id;
      let epic_has_changed: boolean = false;
      const datasource_length: number = this.dataSource.data.length;
      while (!epic_has_changed && i < datasource_length) {
        epic_has_changed = epic_to_check_id !== this.dataSource.data[i].company_product_epic_id;
        if (!epic_has_changed) {
          task_idxs_to_process.push(i);
        }

        // iterem
        i++;
      }
    } else if (dragData.type === 'task') {
      if (previousIndex > -1) task_idxs_to_process = [previousIndex];
    } else throw new Error('dragData.type not valid.');

    return task_idxs_to_process;
  }

  private calcPhaseOrderAfterDropping(previousIndex: number, currentIndex: number, task_idxs_to_process: number[]) {
    const body: any = {};
    const target_order = this.dataSource.data[currentIndex].order_company_product_phase;
    const goingDown = currentIndex > previousIndex;

    // Ajustamos el índice de destino según la dirección del movimiento
    let adjusted_current_index = currentIndex;

    if (goingDown) {
      const count = task_idxs_to_process.filter(i => i < currentIndex).length;
      adjusted_current_index = currentIndex - count + 1;
    } else {
      const count = task_idxs_to_process.filter(i => i < currentIndex).length;
      adjusted_current_index = currentIndex - count;
    }

    // Recalculamos orden para cada ítem
    task_idxs_to_process.forEach((prev_idx, i) => {
      const task_id = this.dataSource.data[prev_idx].id;
      body[task_id] = target_order + adjusted_current_index - currentIndex + i;
    });

    return body;
  }

  private submitBodyToUpdateOrdersAfterDroppingAndUpdateNewOrders(body: any) {
    this.api.changePhaseTasksOrder(this.company_product_phase_id, body).subscribe(data => {
      Object.keys(data).forEach(id => {
        const idx = this.dataSource.data.findIndex(item => item.id === +id);
        if (idx !== -1) {
          this.dataSource.data[idx].order_company_product_phase = (data as any)[id];
        }
      });
      this.evaluateCompanyProductPhaseMinAndMaxOrders();
      this.drag_disabled = false;

      setTimeout(() => {
        for (const task_id of Object.keys(body)) {
          const element = document.querySelector(
            `app-company-product-phase-backlog .mat-mdc-table tr[data-task-id="${task_id}"]`
          ) as HTMLElement;
          if (element) {
            // Paso 1: fuerza el color sin transición
            element.style.transition = 'none';
            element.classList.add('drag-success');

            // Paso 2: activa transición tras un tick
            requestAnimationFrame(() => {
              element.style.transition = 'background-color 1s ease-in-out';

              // Paso 3: quita la clase (desvanece el color)
              setTimeout(() => {
                element.classList.remove('drag-success');
              }, 50); // esperar solo un poco para que el cambio no sea instantáneo
            });
          }
        }
      });
    });
  }

  private async askForTaskOriginCategory(task: ProductTaskCustom, forceDraft: boolean): Promise<ProductTask | undefined> {
    const dialogRef = this.dialog.open(CreateProductTaskDialogComponent, {
      width: '600px',
      data: {
        title: task.title,
        type: task.type,
        company_product_phase_id: this.company_product_phase_id,
        company_product_id: this.company_product_id,
        company_product_epic_id: task.company_product_epic_id,
        order_company_product_phase: task.order_company_product_phase,
        is_draft: forceDraft ? true : task.is_draft,
        force_draft: this.user_permissions().can_create_tasks_on_backlog && !this.user_permissions().can_mark_tasks_as_ready,
        is_hotfix: task.is_hotfix,
        needs_testing: task.needs_testing,
      },
    });

    return await dialogRef.afterClosed().toPromise();
  }

  /**
   * Generates the current prediction state for the given roadmap columns and tasks.
   * Used to inform the backend about the last assigned company_product task and remaining seconds to assign
   * for each distributable (WorkingTimeCategory or CompanyProductTeam), using only the most recent company_product
   * phase board where that distributable had assigned time.
   * This is sent to the backend when requesting the roadmap, so the backend can continue the prediction
   * from the current frontend state. If not provided, the backend will generate a default prediction state.
   *
   * @returns PredictionState[] Example:
   *   [
   *     {
   *       distributable_type: 'App\\Models\\WorkingTimeCategory',
   *       distributable_id: 123,
   *       last_product_task_id: 456,
   *       last_sprint_assigned: 789,
   *       seconds_remaining_to_assign_to_last_sprint: 3600
   *     },
   *     {
   *       distributable_type: 'App\\Models\\CompanyProductTeam',
   *       distributable_id: 321,
   *       last_product_task_id: 654,
   *       last_sprint_assigned: 987,
   *       seconds_remaining_to_assign_to_last_sprint: 0
   *     }
   *   ]
   */
  private buildPredictionState(): PredictionState[] {
    const predictionStateByDistributable = new Map<string, PredictionState>();
    const latestBoardEndDateByDistributable = new Map<string, dayjs.Dayjs>();

    const boardColumns = Object.values(this.roadmap_columns_data).filter(
      (col): col is RoadmapColumnData & { company_product_phase_board: CompanyProductPhaseBoardCustom } =>
        col.company_product_phase_board != null
    );

    for (const col of boardColumns) {
      const company_product_phase_board_id = col.company_product_phase_board.id!;
      const board_budgets = col.product_task_board_time_budgets;
      const board_end_date = dayjs(col.end_date);

      for (const [budgetable_key] of Object.entries(board_budgets)) {
        const [distributable_type, distributable_id_str] = budgetable_key.split('_');
        let last_product_task_id: number | null = null;
        let total_assigned = 0;

        for (let i = 0; i < this.dataSource.data.length; i++) {
          const task = this.dataSource.data[i];

          if (
            task.company_product_phase_boards &&
            task.company_product_phase_boards['company_product_phase_board_' + company_product_phase_board_id] &&
            task.company_product_phase_boards['company_product_phase_board_' + company_product_phase_board_id].distributables[
              budgetable_key
            ]
          ) {
            const dist =
              task.company_product_phase_boards['company_product_phase_board_' + company_product_phase_board_id].distributables[
                budgetable_key
              ];
            total_assigned += dist.total_time_assigned_in_seconds ?? 0;
            last_product_task_id = +task.id!;
          }
        }

        if (last_product_task_id == null) {
          continue;
        }

        const latestBoardEndDate = latestBoardEndDateByDistributable.get(budgetable_key);
        if (latestBoardEndDate != null && !board_end_date.isAfter(latestBoardEndDate)) {
          continue;
        }

        const total_budget = board_budgets[budgetable_key].total_time_budget_in_seconds ?? 0;
        const seconds_remaining_to_assign_to_last_sprint = Math.max(0, total_budget - total_assigned);

        predictionStateByDistributable.set(budgetable_key, {
          distributable_type: distributable_type as PredictionState['distributable_type'],
          distributable_id: +distributable_id_str,
          last_product_task_id,
          last_sprint_assigned: +company_product_phase_board_id,
          seconds_remaining_to_assign_to_last_sprint,
        });
        latestBoardEndDateByDistributable.set(budgetable_key, board_end_date);
      }
    }

    return Array.from(predictionStateByDistributable.values());
  }

  private async initPermissions(): Promise<void> {
    this.isClientDeveloper = this.permissions.isClientDeveloper();
    this.isClientBusiness = this.permissions.isClientBusiness();
    // If URL has no epics preference, default client business to "only epics".
    if (this.filterForm.value.epics == null) {
      this.show_epics_only = this.isClientBusiness;
      this.filterForm.patchValue({ epics: this.show_epics_only ? 1 : 0 }, { emitEvent: false });
    } else {
      this.show_epics_only = +this.filterForm.value.epics === 1;
    }

    await firstValueFrom(this.user_permissions_request$);

    this.refreshAllTasksCanBeAddedToCurrentBoard();
  }

  private recalculateEpicsOnlyColspans() {
    const firstRoadmapColIndex = this.displayedColumns.findIndex(col => col.startsWith('roadmap_'));
    if (firstRoadmapColIndex <= 0) {
      this.epic_title_colspan_for_epics_only = 1;
      this.epic_trailing_colspan_for_epics_only = 0;
      return;
    }

    this.epic_title_colspan_for_epics_only = Math.max(1, firstRoadmapColIndex - 1);
    const roadmapColumnsCount = this.displayedColumns.filter(col => col.startsWith('roadmap_')).length;
    this.epic_trailing_colspan_for_epics_only = Math.max(0, this.displayedColumns.length - (firstRoadmapColIndex + roadmapColumnsCount));
  }

  /**
   * Epic-only row must align with roadmap header columns: one aggregate per sprint **and** per budgetable key.
   * Previously we summed all distributables into one merged cell, so e.g. "dev" header (104h) looked wrong vs
   * epic cell (113.7h) that actually included dev + UX (and any other keys).
   */
  epicRoadmapAggregateCellKey(roadmapColumnKey: string, budgetableKey: string): string {
    return `${roadmapColumnKey}::${budgetableKey}`;
  }

  taskEpicAggregateKey(task: ProductTaskCustom): string {
    return task.epic_group_key ?? this.getEpicGroupKey(task.company_product_epic_id, 0);
  }

  private recalculateEpicRoadmapRegisteredHours() {
    const result: EpicRoadmapHoursByEpic = {};
    const roadmapColumnEntries = Object.entries(this.roadmap_columns_data);
    const nowTimestamp = this.now.getTime();

    this.dataSource.data.forEach(task => {
      const epicKey = this.taskEpicAggregateKey(task);

      if (!result[epicKey]) {
        result[epicKey] = {};
      }

      roadmapColumnEntries.forEach(([roadmapColumnKey, columnData]) => {
        const isCurrentSprint = columnData.active_this_week;
        const isFutureSprint = columnData.start_date.getTime() > nowTimestamp;
        const isPastSprint = !isCurrentSprint && !isFutureSprint;

        const budgetableKeys = Object.keys(columnData.product_task_board_time_budgets ?? {});

        for (const budgetableKey of budgetableKeys) {
          const cellKey = this.epicRoadmapAggregateCellKey(roadmapColumnKey, budgetableKey);
          let valueForTaskAndCell = 0;

          if (columnData.company_product_phase_board?.id) {
            const boardKey = 'company_product_phase_board_' + columnData.company_product_phase_board.id;
            const distributable = task.company_product_phase_boards?.[boardKey]?.distributables?.[budgetableKey];
            if (distributable) {
              if (isPastSprint) {
                valueForTaskAndCell = distributable.total_time_done_in_hours ?? 0;
              } else if (isCurrentSprint) {
                valueForTaskAndCell = distributable.total_time_assigned_in_hours ?? 0;
              } else {
                valueForTaskAndCell = distributable.total_time_budget_in_hours ?? 0;
              }
            }
          } else if (columnData.employee_calendar_register?.id) {
            const reviewKey = 'employee_calendar_register_' + columnData.employee_calendar_register.id;
            const distributable = task.future_sprint_reviews?.[reviewKey]?.distributables?.[budgetableKey];
            if (distributable) {
              valueForTaskAndCell = distributable.total_time_assigned_in_hours ?? 0;
            }
          }

          result[epicKey][cellKey] = (result[epicKey][cellKey] ?? 0) + valueForTaskAndCell;
        }
      });
    });

    this.epic_roadmap_registered_hours = result;
  }

  private getEpicGroupKey(epicId: number | null | undefined, groupIndex: number): string {
    const normalizedEpicKey = epicId == null ? 'no_epic' : epicId.toString();
    return `${normalizedEpicKey}__group_${groupIndex}`;
  }

  onEpicsToggleChange(checked: boolean) {
    this.show_epics_only = checked;
    this.filterForm.patchValue({ epics: checked ? 1 : 0 });
  }

  private initMe() {
    this.me = this.user.getCurrentUser();
  }

  private syncTaskCompletedStatus(task: ProductTaskCustom): void {
    const budgetables = Object.values(task.budgetables);
    const active_budgetables = budgetables.filter(budgetable => (budgetable.total_time_budget_in_seconds ?? 0) > 0);

    if (active_budgetables.length === 0) {
      task.completed = false;
      this.refreshTaskCanBeAddedToCurrentBoard(task);
      return;
    }

    task.completed = active_budgetables.every(budgetable => budgetable.completed_by_user_id !== null);
    this.refreshTaskCanBeAddedToCurrentBoard(task);
  }

  private initBudgetablesForTask(task: ProductTask, item: CompanyProductPhaseBacklogDataProductTask | null = null) {
    const task_level_distributables = item != null ? this.getTaskLevelDistributablesFromBacklogItem(item.distributables) : {};

    const res =
      item != null
        ? this.distributablesToDistributableCustom(
            task_level_distributables,
            undefined,
            task_level_distributables,
            task as ProductTaskCustom
          )
        : {};

    const should_init_placeholder_budgetables = Object.keys(res).length === 0 && !task.is_archived && (!item || !item.completed);

    if (should_init_placeholder_budgetables) {
      this.company_product_phase_task_time_autocalc_settings.forEach(ppttas => {
        if (ppttas.company_product_phase_task_time_autocalc_setting_category.working_time_category != null && ppttas.prcnt > 0) {
          const budgetable_key: string =
            'App\\Models\\WorkingTimeCategory_' + ppttas.company_product_phase_task_time_autocalc_setting_category.working_time_category.id;

          if (!(budgetable_key in res)) {
            res[budgetable_key] = this.createDistributableCustomFromSetting(ppttas);
          }
        }
      });
    }

    return res;
  }

  // Verifica si hay un queryParam 'selectedIssue' y abre el modal inmediatamente
  private checkAndOpenTaskFromParams(): void {
    const selectedIssue = this.activatedRoute.snapshot.queryParamMap.get('selectedIssue');

    if (selectedIssue && !this.hasOpenedTaskFromParams) {
      this.hasOpenedTaskFromParams = true;
      // El dialog cargará sus propios datos de forma independiente
      this.openDialog(selectedIssue);
    }

    // Suscribirse a cambios futuros en queryParams (para cuando se cambia de tarea)
    this.activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(queryParams => {
      const newSelectedIssue = queryParams.get('selectedIssue');

      if (newSelectedIssue && !this.hasOpenedTaskFromParams) {
        this.hasOpenedTaskFromParams = true;
        this.openDialog(newSelectedIssue);
      } else if (!newSelectedIssue) {
        this.hasOpenedTaskFromParams = false;
      }
    });
  }

  downloadRoadmapAsExcel(): void {
    const filters = this.filterForm.value;
    const dialog_data: RoadmapExportDialogData = {
      from: filters.from,
      to: filters.to,
      is_epics_request: this.show_epics_only,
    };

    this.dialog
      .open(RoadmapExportDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
        data: dialog_data,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: RoadmapExportDialogData | undefined) => {
        if (result == null) {
          return;
        }

        this.requestRoadmapExcelExport(result);
      });
  }

  private requestRoadmapExcelExport(export_data: RoadmapExportDialogData): void {
    const params = {
      from: dayjs(export_data.from).format('YYYY-MM-DD'),
      to: dayjs(export_data.to).format('YYYY-MM-DD'),
      is_epics_request: export_data.is_epics_request ? '1' : '0',
    };

    this.api
      .requestCompanyProductPhaseRoadmapExcelByEmail(this.company_product_phase_id, params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.snack.open(`El archivo se está generando. Cuando esté listo te lo enviaremos a ${this.me.email}`, 'Ok', {
            duration: 10000,
          });
        },
      });
  }

  private editTaskIfNeeded(task: ProductTask, idx: number) {
    const current_task: ProductTaskCustom = this.dataSource.data[idx] as ProductTaskCustom;
    const editable_fields: (keyof ProductTask)[] = this.editable_fields_in_task_detail_and_then_displayed;

    for (const field of editable_fields) {
      if (field == 'is_completed' && task.is_completed != null) {
        if (current_task.completed != task.is_completed) {
          current_task.completed = task.is_completed;
        }
      } else if (field == 'product_task_time_budgets') {
        for (const budgetable of Object.values(current_task.budgetables)) {
          const pttb = task.product_task_time_budgets.find(
            (pttb: ProductTaskTimeBudget) => pttb.budgetable_id == budgetable.distributable.id
          );

          if (pttb != null && (pttb.completed_by_user_id == null) !== (budgetable.completed_by_user_id == null)) {
            budgetable.completed_by_user_id = pttb.completed_by_user_id;
          }
        }
      } else {
        if (current_task[field] != task[field]) {
          (current_task as Record<keyof ProductTask, unknown>)[field] = task[field];

          if (field == 'description') {
            this.onDescriptionUpdated(idx);
          } else if (field == 'company_product_epic_id') {
            this.onCompanyProductEpicIdUpdated(idx);
          } else if (field == 'is_draft') {
            this.disableDraft(idx);
          }
        }
      }
    }

    this.cdr.markForCheck();
  }

  private getTaskNavMoveAbilitiesInBacklog(task_code_id: number | string): TaskNavMoveAbilities {
    const task_index = this.dataSource.data.findIndex(task => task.id === +task_code_id);
    return {
      up: task_index > 0,
      down: task_index < this.dataSource.data.length - 1,
      left: false,
      right: false,
    };
  }
}

interface ProductTaskCustom extends ProductTask {
  has_dead_time: boolean;
  description_markdown: string;
  editing: boolean;
  total_time_in_seconds: number;
  total_time_in_hours: number;
  is_first_epic: boolean;
  epic_row_is_even: boolean;
  epic_group_key?: string;
  type_image_url: string;
  editing_description: boolean;
  selected: boolean;
  completed: boolean;
  has_time_registered: boolean;
  can_edit: boolean;
  can_archive_or_delete: boolean;
  can_be_added_to_current_board: boolean;

  disabled_time_autocalc: boolean;
  budgetables: { [key: string]: DistributableCustom };
  company_product_phase_boards: {
    [key: string]: {
      product_task_board_id: number;
      company_product_phase_board: CompanyProductPhaseBoardCustom;
      distributables: {
        [key: string]: DistributableCustom;
      };
    };
  };
  future_sprint_reviews:
    | {
        [key: string]: CompanyProductPhaseBacklogDataProductTaskFutureSprintReview;
      }
    | undefined;
}

interface CompanyProductPhaseTaskTimeAutocalcSettingCustom extends CompanyProductPhaseTaskTimeAutocalcSetting {
  key: string;
  icon: string;
  color: string;
  background_soft_color: string;
  progress_bar_color: string;
  left: string;
  name: string;
  type: 'CompanyProductPhaseTaskTimeAutocalcSettingCustom';
}

interface CompanyProductTeamCustom extends CompanyProductTeam {
  color: string;
  background_soft_color: string;
  progress_bar_color: string;
  key: string;
  icon: string;
  left: undefined;
  company_product_phase_task_time_autocalc_setting_category: undefined;
  type: 'CompanyProductTeamCustom';
}

interface CdkDragDataCustom {
  type: 'task' | 'epic';
  idx: number;
  task: ProductTaskCustom | undefined;
  epic: CompanyProductEpic | undefined;
}

export interface CompanyProductPhaseBacklogData {
  product_tasks: CompanyProductPhaseBacklogDataProductTask[];
  company_product_phase_boards?: {
    company_product_phase_board: CompanyProductPhaseBoard;
    distributables: {
      [key: string]: Distributable;
    };
    weeks: { [key: string]: CompanyProductPhaseBacklogDataWeekRow };
  }[];
  future_sprint_reviews?: {
    employee_calendar_register: EmployeeCalendarRegister;
    start_date: Date;
    end_date: Date;
    distributables: {
      [key: string]: Distributable;
    };
    weeks: { [key: string]: CompanyProductPhaseBacklogDataWeekRow };
  }[];
}

interface CompanyProductPhaseBacklogDataProductTask {
  product_task: ProductTask;
  distributables: {
    [key: string]: Distributable;
  };
  company_product_phase_boards: {
    [key: string]: {
      product_task_board_id: number;
      company_product_phase_board: CompanyProductPhaseBoard;
      distributables: {
        [key: string]: Distributable;
      };
    };
  };
  future_sprint_reviews:
    | {
        [key: string]: CompanyProductPhaseBacklogDataProductTaskFutureSprintReview;
      }
    | undefined;
  completed: boolean;
  has_time_registered: boolean;
}

interface CompanyProductPhaseBacklogDataProductTaskFutureSprintReview {
  employee_calendar_register_id: number;
  distributables: {
    [key: string]: {
      total_time_assigned_in_hours: number;
      total_time_assigned_in_seconds: number;
    };
  };
}

interface Distributable {
  product_task_time_budget_id: number | undefined;
  distributable: WorkingTimeCategory | CompanyProductTeam;
  total_time_budget_in_seconds: number;
  total_time_assigned_in_seconds: number;
  total_time_done_in_seconds: number;
  total_time_budget_in_hours: number;
  total_time_assigned_in_hours: number;
  total_time_done_in_hours: number;
  completed_by_user_id: number | null;
  product_task_board_time_budget_id: number | undefined;
}

interface DistributableCustom extends Distributable {
  in_use_for_autocalc: boolean;
  company_product_phase_task_time_autocalc_setting_id: number;
  color: string;
  background_soft_color: string;
  progress_bar_color: string;
  readonly: boolean;
  type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam';
  editing: boolean;
}

interface RoadmapColumnData {
  company_product_phase_board: CompanyProductPhaseBoardCustom | undefined;
  employee_calendar_register: EmployeeCalendarRegister | undefined;
  start_date: Date;
  end_date: Date;
  product_task_board_time_budgets: { [key: string]: DistributableCustom };
  active_this_week: boolean;
  is_present_or_future: boolean;
  weeks: { [key: string]: CompanyProductPhaseBacklogDataWeekRow };
}

type RoadmapCellHoursMap = { [cellKey: string]: number };
type EpicRoadmapHoursByEpic = { [epicKey: string]: RoadmapCellHoursMap };

interface BacklogFetchRequest {
  params: BacklogFetchQueryParams;
  query: Record<string, unknown>;
  request_id: number;
}

interface BacklogFetchResult {
  data: CompanyProductPhaseBacklogData;
  prepend_or_append: PrependOrAppendEnum;
  request_id: number;
}

interface BacklogFetchQueryParams {
  prepend_or_append: PrependOrAppendEnum;
  min_order: number | undefined;
  max_order: number | undefined;
  from: Date;
  to: Date;
}

type PrependOrAppendEnum = 'v_prepend' | 'v_append' | 'h_prepend' | 'h_append' | 'first_time';

interface CompanyProductPhaseBacklogDataWeekRow {
  company_product_phase_weekly_working_time_budget_distribution_distributable: number;
  weekly_working_time_budget_id: number;
  start_week_day: Date;
}

interface CompanyProductPhaseBoardCustom extends CompanyProductPhaseBoard {
  is_deletable: boolean;
}

interface PredictionState {
  distributable_type: 'App\\Models\\WorkingTimeCategory' | 'App\\Models\\CompanyProductTeam';
  distributable_id: number;
  last_product_task_id: number;
  last_sprint_assigned: number;
  seconds_remaining_to_assign_to_last_sprint: number;
}
